
import { v4 as uuidv4 } from "uuid";
import { 
  ModelBase,
} from "@models/UpsertableBase";
import { 
  IStringHash,
  FeatureBase,
} from "@models";
import { 
  Sense,
  TemplateBase,
  IStringUpgradableNumberHash,
  UpgradableNumber,
  HitDiceUpgradable,
  AbilityScoresUpgradable,
  HitDice,
  AttachedTable,
  AttributesTable,
  Attributes,
  SpellcastingTable,
  CreatureLimitation,
  AbilityLimitation,
  AbilityEffect,
  LegendariesTable,
  SKILLS,
} from "@dndModels";
import { 
  CREATURE_TYPES,
  DAMAGE_TYPES, 
  HIT_DICE_SIZE_MAP, 
  SIZE_TO_NUM_MAP 
} from "@dndModels/Constants";
import { AbilityScoreType, AttackType, CreatureType, DamageType, DataType, WorldSetting } from "@dndModels/Types";

import CommonUtilities from "@utilities/common_utilities";
// import DandDAPI from "../../utilities/system_api";

// const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export class Creature extends ModelBase implements CreatureDetails {
  data_type: DataType = "creature";
  imported_object: any = {};
  connected = false;

  title = "";
  small_name = "";
  flavor_text = "";
  name_table: { level: number, name: string, age_category: string }[] = [];
  // home
  challenge_rating = 0;
  min_challenge_rating = 0;
  max_challenge_rating = 0;

  get cr_string() {
    if (this.min_challenge_rating === this.max_challenge_rating) {
      return `${this.challenge_rating}`;
    } else {
      return `${this.min_challenge_rating} - ${this.max_challenge_rating}`;
    }
  }

  creature_type: CreatureType = "None"; // Beast, Dragon, Humanoid, etc.
  subtypes: string[] = []; // Drow, Bronze, etc.
  class_ids: string[] = [];
  age_category = ""; // Adult, etc.

  hit_points_called = "";
  hit_dice: HitDiceUpgradable[] = [];
  hit_dice_bonus: UpgradableNumber = new UpgradableNumber({ add_ability_scores: ["CON"], add_ability_mod_mult: 1 });
  extra_hp_pools: { true_id: string, called: string, max_hp: UpgradableNumber }[] = []; // Adding this for Kaiju Vulnerability Points, but could be used for other things
  finishing_blow: UpgradableNumber = new UpgradableNumber();
  starting_hp: UpgradableNumber | null = null;

  parse_errors: string[] = [];

  table: AttachedTable = new AttachedTable();

  name_for_cr(cr: number) {
    const entries = this.name_table.filter(o => o.level <= cr);
    if (entries.length > 0) {
      let entry = entries.find(o => o.level === cr);
      if (!entry) {
        const max = Math.max(...entries.map(o => o.level), 0);
        entry = entries.find(o => o.level === max);
      }
      if (entry) {
        return entry.name;
      }
    }
    return this.get_use_name();
  }
  age_category_for_cr(cr: number) {
    const entries = this.name_table.filter(o => o.level <= cr);
    if (entries.length > 0) {
      let entry = entries.find(o => o.level === cr);
      if (!entry) {
        const max = Math.max(...entries.map(o => o.level), 0);
        entry = entries.find(o => o.level === max);
      }
      if (entry) {
        return entry.age_category;
      }
    }
    return this.age_category;
  }
  max_hp_for_cr(cr: number, log_it = false) {
    const entry = this.table.getEntry(cr);

    const size = this.size_num_for_cr(cr);
    const hd_size = HIT_DICE_SIZE_MAP[size];
    if (log_it) {
      console.log(size);
      console.log(hd_size);
    }

    const hit_dice = this.hit_dice.map(o => {
      const hd = new HitDice();
      hd.count = o.count.base;
      hd.size = hd_size;
      if (!o.count.isBasic()) {
        // if it's not basic it's usually just a CR multiplier,
        // and we don't want to worry about anything else.
        hd.count += o.count.add_char_level_mult * cr;
        if (o.count.table_field !== "") {
          if (entry) {
            const dice_count_data = entry.data[o.count.table_field];
            if (dice_count_data) {
              hd.count += dice_count_data;
            }
          }
        }
      }
      return hd;
    });
    let hit_dice_bonus = this.hit_dice_bonus.base;
    // if (this.name.includes("Vine Dragon Wyrm")) {
    //   debugger;
    // }
    if (!this.hit_dice_bonus.isBasic()) {
      // It may have a CON and or CR multiplier
      hit_dice_bonus += this.hit_dice_bonus.add_char_level_mult * cr;
      if (this.hit_dice_bonus.add_ability_mod_mult !== 0) {
        let con = this.ability_scores.CON.base;
        if (!this.ability_scores.CON.isBasic()) {
          con += this.ability_scores.CON.add_char_level_mult * cr;
          if (this.ability_scores.CON.table_field !== "") {
            if (entry) {
              const con_data = entry.data[this.ability_scores.CON.table_field];
              if (con_data) {
                con += con_data;
              }
            }
          }
        }
        con = Math.floor((con - 10) / 2);
        hit_dice_bonus += this.hit_dice_bonus.add_ability_mod_mult * con;
      }
    }
    return hit_dice.length === 0 ? hit_dice_bonus : Math.floor(
      hit_dice.map(o => {
        return o.count * ((o.size + 1) / 2) + o.count * hit_dice_bonus;
      }).reduce((a,b) => a + b, 0) // + this.hit_dice_bonus
    );
  }
  hit_dice_string_for_cr(cr: number) {
    const entry = this.table.getEntry(cr);

    const hit_dice = this.hit_dice.map(o => {
      const hd = new HitDice();
      hd.count = o.count.base;
      hd.size = o.size;
      if (!o.count.isBasic()) {
        // if it's not basic it's usually just a CR multiplier,
        // and we don't want to worry about anything else.
        hd.count += o.count.add_char_level_mult * cr;
        if (o.count.table_field !== "") {
          if (entry) {
            const dice_count_data = entry.data[o.count.table_field];
            if (dice_count_data) {
              hd.count += dice_count_data;
            }
          }
        }
      }
      return hd;
    });
    let hit_dice_bonus = this.hit_dice_bonus.base;
    // if (this.name.includes("Vine Dragon Wyrm")) {
    //   debugger;
    // }
    if (!this.hit_dice_bonus.isBasic()) {
      // It may have a CON and or CR multiplier
      hit_dice_bonus += this.hit_dice_bonus.add_char_level_mult * cr;
      if (this.hit_dice_bonus.add_ability_mod_mult !== 0) {
        let con = this.ability_scores.CON.base;
        if (!this.ability_scores.CON.isBasic()) {
          con += this.ability_scores.CON.add_char_level_mult * cr;
          if (this.ability_scores.CON.table_field !== "") {
            if (entry) {
              const con_data = entry.data[this.ability_scores.CON.table_field];
              if (con_data) {
                con += con_data;
              }
            }
          }
        }
        con = Math.floor((con - 10) / 2);
        hit_dice_bonus += this.hit_dice_bonus.add_ability_mod_mult * con;
      }
    }
    const max_hp = hit_dice.length === 0 ? hit_dice_bonus : Math.floor(
      hit_dice.map(o => {
        return o.count * ((o.size + 1) / 2) + o.count * hit_dice_bonus;
      }).reduce((a,b) => a + b, 0) // + this.hit_dice_bonus
    );
    return hit_dice.length === 1 ? `${max_hp} (${hit_dice[0].count}d${hit_dice[0].size} + ${hit_dice[0].count * hit_dice_bonus})` : `${max_hp}`;
  }
  ac_for_cr(cr: number) {
    const entry = this.table.getEntry(cr);
    let ac = this.armor_class.base;
    if (this.armor_class.add_ability_mod_mult > 0) {
      for (const stat of this.armor_class.add_ability_scores) {
        const score = this.ability_scores.getAbilityScore(stat);
        if (score) {
          let flat_score = score.base;
          if (!score.isBasic()) {
            if (score.table_field === stat && entry) {
              flat_score = entry.data[stat];
            }
          }
          const mod = Math.floor((flat_score - 10) / 2);
          ac += mod * this.armor_class.add_ability_mod_mult;
        }
      }
    }
    if (this.armor_class.table_field !== "" && entry) {
      ac += entry.data[this.armor_class.table_field];
    }
    return ac;
  }
  size_num_for_cr(cr: number) {
    let size = this.size_ugn.base;
    if (!this.size_ugn.isBasic()) {
      const entry = this.table.getEntry(cr);
      if (this.size_ugn.table_field !== "" && entry) {
        size += entry.data[this.size_ugn.table_field];
      }
    }
    return size;
  }
  legendary_resistances_for_cr(cr: number) {
    const entry = this.legendaries_table.getEntry(cr);
    if (entry) {
      return entry.resistances;
    }
    return 0;
  }
  legendary_actions_for_cr(cr: number) {
    const entry = this.legendaries_table.getEntry(cr);
    if (entry) {
      return entry.actions;
    }
    return 0;
  }

  get min_max_hp() {
    const cr = this.min_challenge_rating;
    return this.max_hp_for_cr(cr);
  }

  get standard_max_hp() {
    let cr = this.min_challenge_rating;
    let hp = this.max_hp_for_cr(cr);
    
    const hps: string[] = [];
    if (this.challenge_rating !== this.min_challenge_rating) {
      hps.push(`Min: ${hp}`);
      cr = this.challenge_rating;
      hp = this.max_hp_for_cr(cr);
      hps.push(`Default: ${hp}`);
      if (this.challenge_rating !== this.max_challenge_rating) {
        cr = this.max_challenge_rating;
        hp = this.max_hp_for_cr(cr);
        hps.push(`Max: ${hp}`);
      }
    } else if (this.challenge_rating !== this.max_challenge_rating) {
      hps.push(`Default: ${hp}`);
      cr = this.max_challenge_rating;
      hp = this.max_hp_for_cr(cr);
      hps.push(`Max: ${hp}`);
    } else {
      hps.push(`${hp}`);
    }
    return hps.join(",");
  }

  initiative_modifier: UpgradableNumber = new UpgradableNumber();
  armor_class: UpgradableNumber = new UpgradableNumber();
  ac_flavor = "";
  get size_num() {
    return this.size_num_for_cr(this.challenge_rating);
  }
  size_ugn: UpgradableNumber = new UpgradableNumber(3);
  alignment = "";
  damage_threshold = 0;

  spellcasting_ability: AbilityScoreType | "None" = "None";
  dc: UpgradableNumber = new UpgradableNumber();
  magic_attack: UpgradableNumber = new UpgradableNumber();
  non_magic_attack: UpgradableNumber = new UpgradableNumber();
  magic_dmg_bonus: UpgradableNumber = new UpgradableNumber();
  non_magic_dmg_bonus: UpgradableNumber = new UpgradableNumber();

  string_hash: IStringHash = {};
  upgradable_hash: IStringUpgradableNumberHash = {};

  attributes_table: AttributesTable = new AttributesTable();
  
  ability_scores: AbilityScoresUpgradable = new AbilityScoresUpgradable();
  resources_table: AttachedTable = new AttachedTable();
  spellcasting_table: SpellcastingTable = new SpellcastingTable();
  legendaries_table: LegendariesTable = new LegendariesTable();

  fbs: FeatureBase[] = [];
  // action_fbs: FeatureBase[] = [];
  // traits: FeatureBase[] = [];
  // legendary_action_fbs: FeatureBase[] = [];
  // lair_action_fbs: FeatureBase[] = [];

  get action_fbs() {
    return this.fbs.filter(o => o.creature_fb_type === "Actions");
  }
  get traits() {
    return this.fbs.filter(o => o.creature_fb_type === "Traits");
  }
  get legendary_action_fbs() {
    return this.fbs.filter(o => o.creature_fb_type === "Legendary Actions");
  }
  get lair_action_fbs() {
    return this.fbs.filter(o => o.creature_fb_type === "Lair Actions");
  }

  environments: string[] = [];
  // settings: string[] = []; // 
  native_planes: string[] = []; 

  group_id = "";
  gear_item_ids: string[] = [];

  // // Vehicles stuff for Space Unjammed by Trekiros
  // ridable_type: "Mount" | "Vehicle" | "None" = "None";
  // max_passengers: number = 0;
  // attunement_slots: number = 0;
  // cost: number = 0;
  // mishap_description: string = "";
  // mishaps: Feature[] = [];
  // stations: Feature[] = [];

  // Simple Characters are Creatures made from Characters.  
  // The purpose is for making them simple, for use as Minions, 
  // or for Creatures to be controlled by the GM, 
  // which are better off simpler than full characters 
  // because the GM needs to do a lot more than a player.
  simple_character = false;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.imported_object = obj.imported_object ? obj.imported_object : null;

      this.title = obj.title ? obj.title : "";
      if (this.title === "" && this.name.includes(",")) {
        const pieces = this.name.split(",");
        if (pieces.length === 2) {
          this.name = pieces[0].trim();
          this.title = pieces[1].trim();
        }
      }

      (this.creature_type as string) = obj.creature_type ? common_util.capitalize_firsts(obj.creature_type) : (obj.type ? common_util.capitalize_firsts(obj.type) : "None");
      if (!CREATURE_TYPES.includes(this.creature_type)) {
        this.creature_type = "None";
      }
      this.subtypes = obj.subtypes ? obj.subtypes : [];
      this.age_category = obj.age_category ? obj.age_category : "";
      let subtype = obj.subtype ? common_util.capitalize_firsts(obj.subtype) : "";
      if (this.subtypes.length === 0 && subtype === "") {
        if (this.age_category === "" && this.name.includes("Dragon")) {
          const pieces = this.name.split(" ");
          if (pieces.length === 3 && pieces[2] === "Dragon") {
            this.age_category = pieces[0];
            subtype = pieces[1];
          } else if (pieces.length === 3 && pieces[1] === "Dragon") {
            subtype = pieces[0];
            this.age_category = pieces[2];
          } else if (!["Hollow Dragon", "Ghost Dragon", "Half-Dragon"].includes(this.name)) {
            // debugger;
            // console.log(this.name);
          }
        }
        if (subtype !== "") {
          this.subtypes.push(subtype);
        }
      }
      this.subtypes = common_util.unique_array(this.subtypes);
      this.class_ids = obj.class_ids ? obj.class_ids : [];
      this.challenge_rating = obj.challenge_rating ? obj.challenge_rating : 0;
      this.min_challenge_rating = obj.min_challenge_rating ? obj.min_challenge_rating : this.challenge_rating;
      this.max_challenge_rating = obj.max_challenge_rating ? obj.max_challenge_rating : this.challenge_rating;
      this.name_table = obj.name_table ? obj.name_table : [];

      this.ability_scores = obj.ability_scores ? new AbilityScoresUpgradable(obj.ability_scores) : new AbilityScoresUpgradable(8);
      
      this.resources_table = obj.resources_table ? new AttachedTable(obj.resources_table) : new AttachedTable();
      
      // if (this.name.includes("Ancient Gold Dragon")) {
      //   debugger;
      // }
      this.legendaries_table = obj.legendaries_table ? new LegendariesTable(obj.legendaries_table) : new LegendariesTable();
      
      if (obj.fbs) {
        this.fbs = obj.fbs.map((o: any) => new FeatureBase(o));
      } else {
        if (obj.action_fbs) {
          let fbs: FeatureBase[] = obj.action_fbs.map((o: any) => new FeatureBase(o));
          fbs = fbs.filter(o => o.features.length > 0);
          for (const fb of fbs) {
            fb.creature_fb_type = "Actions";
          }
          this.fbs.push(...fbs);
        } 
        
        if (obj.legendary_action_fbs) {
          let fbs: FeatureBase[] = obj.legendary_action_fbs.map((o: any) => new FeatureBase(o));
          fbs = fbs.filter(o => o.features.length > 0);
          for (const fb of fbs) {
            fb.creature_fb_type = "Legendary Actions";
          }
          this.fbs.push(...fbs);
        } 
        if (obj.lair_action_fbs) {
          let fbs: FeatureBase[] = obj.lair_action_fbs.map((o: any) => new FeatureBase(o));
          fbs = fbs.filter(o => o.features.length > 0);
          for (const fb of fbs) {
            fb.creature_fb_type = "Lair Actions";
          }
          this.fbs.push(...fbs);
        } 
        if (obj.traits) {
          let fbs: FeatureBase[] = obj.traits.map((o: any) => new FeatureBase(o));
          fbs = fbs.filter(o => o.features.length > 0);
          for (const fb of fbs) {
            fb.creature_fb_type = "Traits";
          }
          this.fbs.push(...fbs);
        } 
      }
      
      this.spellcasting_table = obj.spellcasting_table ? new SpellcastingTable(obj.spellcasting_table) : new SpellcastingTable();
      
      this.hit_points_called = obj.hit_points_called ? obj.hit_points_called : "";
      this.hit_dice = []; 
      if (obj.hit_dice) {
        if (obj.hit_dice.length) {
          obj.hit_dice.forEach((hd: any) => {
            this.hit_dice.push(new HitDiceUpgradable(hd));
          });
        } else {
          this.hit_dice.push(new HitDiceUpgradable(obj.hit_dice));
        }
      }
      // Default to CON
      this.hit_dice_bonus = obj.hit_dice_bonus ? new UpgradableNumber(obj.hit_dice_bonus) : new UpgradableNumber({ add_ability_scores: ["CON"], add_ability_mod_mult: 1 });
      
      this.extra_hp_pools = [];
      if (obj.extra_hp_pools) {
        for (const o of obj.extra_hp_pools) {
          this.extra_hp_pools.push({ true_id: o.true_id, called: o.called, max_hp: new UpgradableNumber(o.max_hp) });
        }
      }
      this.finishing_blow = obj.finishing_blow ? new UpgradableNumber(obj.finishing_blow) : new UpgradableNumber();
      this.starting_hp = obj.starting_hp ? new UpgradableNumber(obj.starting_hp) : null;

      this.initiative_modifier = obj.initiative_modifier ? new UpgradableNumber(obj.initiative_modifier) : new UpgradableNumber();
      this.armor_class = obj.armor_class ? new UpgradableNumber(obj.armor_class) : new UpgradableNumber();
      if (this.armor_class.isBlank()) {
        this.armor_class.add_ability_mod_mult = 1;
        this.armor_class.add_ability_scores.push("DEX");
        this.armor_class.base = 10;
      }
      this.ac_flavor = obj.ac_flavor ? obj.ac_flavor : "";
      this.size_ugn = obj.size_ugn ? new UpgradableNumber(obj.size_ugn) : new UpgradableNumber();
      if (this.size_ugn.isBlank()) {
        if (obj.size_num) {
          this.size_ugn.base = obj.size_num;
        } else if (obj.size) {
          this.size_ugn.base = SIZE_TO_NUM_MAP[obj.size];
        } else {
          this.size_ugn.base = 3;
        }
      }
      this.alignment = obj.alignment ? common_util.capitalize_firsts(obj.alignment) : "Neutral";

      this.environments = obj.environments ? obj.environments : [];
      // this.settings = obj.settings ? obj.settings : [];
      if (obj.settings && obj.settings.length > 0 && this.world_settings.length === 0) {
        this.world_settings = obj.settings as WorldSetting[];
      }
      if (this.world_settings.includes("Any")) {
        this.world_settings = this.world_settings.filter(o => o !== "Any");
      }
      this.native_planes = obj.native_planes ? obj.native_planes : [];

      this.group_id = obj.group_id ? obj.group_id : "";
      this.gear_item_ids = obj.gear_item_ids ? obj.gear_item_ids : [];

      this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "";
      this.dc = obj.dc ? new UpgradableNumber(obj.dc) : new UpgradableNumber();
      if (this.dc.isBasic() && this.dc.base === -1) {
        this.dc.base = 0;
      }
      this.magic_attack = obj.magic_attack ? new UpgradableNumber(obj.magic_attack) : new UpgradableNumber();
      if (this.magic_attack.isBasic() && this.magic_attack.base === -1) {
        this.magic_attack.base = 0;
      }
      this.non_magic_attack = obj.non_magic_attack ? new UpgradableNumber(obj.non_magic_attack) : new UpgradableNumber();
      if (this.non_magic_attack.isBasic() && this.non_magic_attack.base === -1) {
        this.non_magic_attack.base = 0;
      }
      this.magic_dmg_bonus = obj.magic_dmg_bonus ? new UpgradableNumber(obj.magic_dmg_bonus) : new UpgradableNumber();
      if (this.magic_dmg_bonus.isBasic() && this.magic_dmg_bonus.base === -1) {
        this.magic_dmg_bonus.base = 0;
      }
      this.non_magic_dmg_bonus = obj.non_magic_dmg_bonus ? new UpgradableNumber(obj.non_magic_dmg_bonus) : new UpgradableNumber();
      if (this.non_magic_dmg_bonus.isBasic() && this.non_magic_dmg_bonus.base === -1) {
        this.non_magic_dmg_bonus.base = 0;
      }
      
      this.string_hash = obj.string_hash ? obj.string_hash : {};
      this.upgradable_hash = {};
      if (obj.upgradable_hash) {
        Object.keys(obj.upgradable_hash).forEach(key => {
          this.upgradable_hash[key] = new UpgradableNumber(obj.upgradable_hash[key]);
        });
      }
      
      this.simple_character = obj.simple_character ? obj.simple_character : false;

      this.table = obj.table ? new AttachedTable(obj.table) : new AttachedTable();
      this.attributes_table = obj.attributes_table ? new AttributesTable(obj.attributes_table) : new AttributesTable();
      if (!obj.attributes_table) {
        const entry = new Attributes();
        entry.level = this.min_challenge_rating;
        entry.speeds = obj.speed ? obj.speed : {
          walk: 0,
          swim: 0,
          climb: 0,
          fly: 0,
          burrow: 0,
          hover: 0
        };

        entry.save_profs = [];
        const saves = obj.saving_throws2 ? obj.saving_throws2 : [];
        if (typeof(saves) === "object") {
          if (saves.length) {
            entry.save_profs = saves;
          } else {
            entry.save_profs = Object.keys(saves);
          }
        }

        entry.languages = obj.languages ? obj.languages : "";
        entry.tool_profs = obj.tool_profs ? obj.tool_profs : []; 
        if (!obj.tool_profs && obj.tool_proficiencies) {
          const tool_proficiencies = obj.tool_proficiencies;
          const keys = Object.keys(tool_proficiencies);
          entry.tool_profs = keys;
        }

        entry.condition_immunities = [];
        if (obj.condition_immunities && obj.condition_immunities.length) {
          obj.condition_immunities.forEach((ci: any) => {
            // This is to fix an issue that happened when I was initially copying and parsing these
            if (typeof(ci) === "string") {
              entry.condition_immunities.push(ci);
            }
          });
        }
        entry.extra_damage_immunities = obj.extra_damage_immunities && typeof(obj.extra_damage_immunities) === "string" ? obj.extra_damage_immunities : "";
        entry.damage_immunities = [];
        if (obj.damage_immunities && obj.damage_immunities.length > 0) {
          entry.damage_immunities = obj.damage_immunities;
        } else if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
          obj.damage_multipliers.filter((o: any) => o.multiplier === 0).forEach((dm: any) => {
            entry.damage_immunities = [...entry.damage_immunities,...dm.damage_types];
          });
        }
        entry.damage_immunities = entry.damage_immunities.map(o => {
          return o[0].toLocaleUpperCase() + o.substring(1);
        });
        if (entry.extra_damage_immunities.length === 0) {
          const non_fitting = entry.damage_immunities.filter(o => !DAMAGE_TYPES.includes(o));
          entry.damage_immunities = entry.damage_immunities.filter(o => DAMAGE_TYPES.includes(o));
          if (non_fitting.length > 0) {
            if (entry.extra_damage_immunities.length > 0) {
              entry.extra_damage_immunities += ", ";
            }
            entry.extra_damage_immunities += non_fitting.join(", ");
          }
        } else {
          entry.damage_immunities = entry.damage_immunities.filter(o => DAMAGE_TYPES.includes(o));
        }
        entry.extra_damage_resistances = obj.extra_damage_resistances && typeof(obj.extra_damage_resistances) === "string" ? obj.extra_damage_resistances : "";
        entry.damage_resistances = [];
        if (obj.damage_resistances && obj.damage_resistances.length > 0) {
          entry.damage_resistances = obj.damage_resistances;
        } else if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
          obj.damage_multipliers.filter((o: any) => o.multiplier === 0.5).forEach((dm: any) => {
            entry.damage_resistances = [...entry.damage_resistances,...dm.damage_types];
          });
        }
        entry.damage_resistances = entry.damage_resistances.map(o => {
          return o[0].toLocaleUpperCase() + o.substring(1);
        });
        if (entry.extra_damage_resistances.length === 0) {
          const non_fitting = entry.damage_resistances.filter(o => !DAMAGE_TYPES.includes(o));
          entry.damage_resistances = entry.damage_resistances.filter(o => DAMAGE_TYPES.includes(o));
          if (non_fitting.length > 0) {
            if (entry.extra_damage_resistances.length > 0) {
              entry.extra_damage_resistances += ", ";
            }
            entry.extra_damage_resistances += non_fitting.join(", ");
          }
        } else {
          entry.damage_resistances = entry.damage_resistances.filter(o => DAMAGE_TYPES.includes(o));
        }
        entry.extra_damage_vulnerabilities = obj.extra_damage_vulnerabilities && typeof(obj.extra_damage_vulnerabilities) === "string" ? obj.extra_damage_vulnerabilities : "";
        entry.damage_vulnerabilities = [];
        if (obj.damage_vulnerabilities && obj.damage_vulnerabilities.length > 0) {
          entry.damage_vulnerabilities = obj.damage_vulnerabilities;
        } else if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
          obj.damage_multipliers.filter((o: any) => o.multiplier === 2).forEach((dm: any) => {
            entry.damage_vulnerabilities = [...entry.damage_vulnerabilities,...dm.damage_types];
          });
        }
        entry.damage_vulnerabilities = entry.damage_vulnerabilities.map(o => {
          return o[0].toLocaleUpperCase() + o.substring(1);
        });
        if (entry.extra_damage_vulnerabilities.length === 0) {
          const non_fitting = entry.damage_vulnerabilities.filter(o => !DAMAGE_TYPES.includes(o));
          entry.damage_vulnerabilities = entry.damage_vulnerabilities.filter(o => DAMAGE_TYPES.includes(o));
          if (non_fitting.length > 0) {
            if (entry.extra_damage_vulnerabilities.length > 0) {
              entry.extra_damage_vulnerabilities += ", ";
            }
            entry.extra_damage_vulnerabilities += non_fitting.join(", ");
          }
        } else {
          entry.damage_vulnerabilities = entry.damage_vulnerabilities.filter(o => DAMAGE_TYPES.includes(o));
        }
        // if (this.name.includes("Vine Dragon Wy")) {
        //   debugger;
        // }
        entry.senses = {};
        if (obj.senses) {
          if (obj.senses.length > 0) {
            obj.senses.forEach((sense: any) => {
              console.log(sense);
              // entry.senses[sense.name] = sense.range;
              // entry.senses.push(new CharacterSense(sense));
            });
          } else {
            entry.senses = obj.senses;
          }
        }
        entry.skill_profs = obj.skill_proficiencies2 ? obj.skill_proficiencies2 : {};
        if (!obj.skill_proficiencies2 && obj.skill_proficiencies) {
          const skill_ids = Object.keys(obj.skill_proficiencies);
          const proficiency_modifier = this.proficiency_modifier;
          const table_entry = this.table.entries.find(o => o.level === this.challenge_rating);
          for (const id of skill_ids) {
            let skill_id = id;
            let skill = SKILLS.find(o => o._id === skill_id);
            if (!skill) {
              skill = SKILLS.find(o => o.name === skill_id);
              if (skill) {
                skill_id = skill._id;
              }
            }
            const old_value = obj.skill_proficiencies[id];
            if (skill && old_value) {
              const ability_score = this.ability_scores.getAbilityScore(skill.use_ability_score);
              if (ability_score) {
                let score = ability_score.base;
                if (!ability_score.isBasic()) {
                  if (ability_score.table_type === "Attached" && table_entry) {
                    score = table_entry.data[skill.use_ability_score];
                  }
                }
                const mod = Math.floor((score - 10) / 2);
                if (old_value === mod + proficiency_modifier) {
                  entry.skill_profs[skill_id] = 1;
                } else if (old_value === mod + proficiency_modifier * 2) {
                  entry.skill_profs[skill_id] = 2;
                } else if (old_value === mod + proficiency_modifier * 0.5) {
                  entry.skill_profs[skill_id] = 0.5;
                }
              }
            }
          }
          // if (this.name === "Mind Flayer") {
          //   console.log(this.skill_proficiencies2);
          // }
        }
        entry.skill_profs = obj.skill_proficiencies2 ? obj.skill_proficiencies2 : {};
        if (!obj.skill_proficiencies2 && obj.skill_proficiencies) {
          const skill_ids = Object.keys(obj.skill_proficiencies);
          const proficiency_modifier = this.proficiency_modifier;
          const table_entry = this.table.entries.find(o => o.level === this.challenge_rating);
          for (const id of skill_ids) {
            let skill_id = id;
            let skill = SKILLS.find(o => o._id === skill_id);
            if (!skill) {
              skill = SKILLS.find(o => o.name === skill_id);
              if (skill) {
                skill_id = skill._id;
              }
            }
            const old_value = obj.skill_proficiencies[id];
            if (skill && old_value) {
              const ability_score = this.ability_scores.getAbilityScore(skill.use_ability_score);
              if (ability_score) {
                let score = ability_score.base;
                if (!ability_score.isBasic()) {
                  if (ability_score.table_type === "Attached" && table_entry) {
                    score = table_entry.data[skill.use_ability_score];
                  }
                }
                const mod = Math.floor((score - 10) / 2);
                if (old_value === mod + proficiency_modifier) {
                  entry.skill_profs[skill_id] = 1;
                } else if (old_value === mod + proficiency_modifier * 2) {
                  entry.skill_profs[skill_id] = 2;
                } else if (old_value === mod + proficiency_modifier * 0.5) {
                  entry.skill_profs[skill_id] = 0.5;
                }
              }
            }
          }
          // if (this.name === "Mind Flayer") {
          //   console.log(this.skill_proficiencies2);
          // }
        }
        this.attributes_table.entries.push(entry);
      }
      // Identify and merge duplicate rows (same level)
      const attribute_levels = common_util.unique_array(this.attributes_table.entries.map(o => o.level));
      if (attribute_levels.length !== this.attributes_table.entries.length) {
        // We've got duplicates.
        for (const level of attribute_levels) {
          const entries_of_level = this.attributes_table.entries.filter(o => o.level === level);
          if (entries_of_level.length > 1) {
            const prime_entry = entries_of_level[0];
            const delete_us: string[] = [];
            for (let i = 1; i < entries_of_level.length; ++i) {
              const delete_me = entries_of_level[i];
              prime_entry.merge(delete_me);
              delete_us.push(delete_me.true_id);
            }
            this.attributes_table.entries = this.attributes_table.entries.filter(o => !delete_us.includes(o.true_id));
          }
        }
      }
      this.small_name = obj.small_name ? obj.small_name : "";
      if (this.small_name === "") {
        switch (this.creature_type) {
          case "Humanoid": break;
          case "Beast": break;
          case "Construct": break;
          case "Aberration": break;
          // case "Undead": break;
          // case "Dragon": break;
          case "Monstrosity": break;
          // case "Elemental": break;
          // case "Fey": break;
          // case "Giant": break;
          // case "Ooze": break;
          // case "Plant": break;
          case "Shapechanger": break;
          // case "Celestial": break;
          // case "Fiend": break;
          default: this.small_name = this.creature_type.toLocaleLowerCase(); break;
        }
      }
      if (this.small_name === "") {
        if (this.name.includes(" ")) {
          const pieces = this.name.split(" ");
          if (pieces.length === 2) {
            this.small_name = pieces[1].toLocaleLowerCase();
          }
        }
        if (this.small_name.length === 0) {
          this.small_name = this.name.toLocaleLowerCase();
        }
      }
      this.flavor_text = obj.flavor_text ? obj.flavor_text : "";

      this._the_attack_types = obj._the_attack_types ? obj._the_attack_types : [];
      this._the_effect_types = obj._the_effect_types ? obj._the_effect_types : [];
      this._the_utility_details = obj._the_utility_details ? obj._the_utility_details : [];
    }
    this.connected = false;
  }

  get_detail_format() {
    const str = `${super.get_detail_format()}; ${this.creature_type_string}, CR: ${this.challenge_rating}, Max HP: ${this.standard_max_hp}`;
    return str.trim();
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.imported_object = obj.imported_object ? obj.imported_object : null;

  //     this.title = obj.title ? obj.title : "";
  //     if (this.title === "" && this.name.includes(",")) {
  //       const pieces = this.name.split(",");
  //       if (pieces.length === 2) {
  //         this.name = pieces[0].trim();
  //         this.title = pieces[1].trim();
  //       }
  //     }

  //     (this.creature_type as string) = obj.creature_type ? common_util.capitalize_firsts(obj.creature_type) : (obj.type ? common_util.capitalize_firsts(obj.type) : "None");
  //     if (!CREATURE_TYPES.includes(this.creature_type)) {
  //       this.creature_type = "None";
  //     }
  //     this.subtypes = obj.subtypes ? obj.subtypes : [];
  //     this.age_category = obj.age_category ? obj.age_category : "";
  //     let subtype = obj.subtype ? common_util.capitalize_firsts(obj.subtype) : "";
  //     if (this.subtypes.length === 0 && subtype === "") {
  //       if (this.age_category === "" && this.name.includes("Dragon")) {
  //         const pieces = this.name.split(" ");
  //         if (pieces.length === 3 && pieces[2] === "Dragon") {
  //           this.age_category = pieces[0];
  //           subtype = pieces[1];
  //         } else if (pieces.length === 3 && pieces[1] === "Dragon") {
  //           subtype = pieces[0];
  //           this.age_category = pieces[2];
  //         } else if (!["Hollow Dragon", "Ghost Dragon", "Half-Dragon"].includes(this.name)) {
  //           // debugger;
  //           console.log(this.name);
  //         }
  //       }
  //       if (subtype !== "") {
  //         this.subtypes.push(subtype);
  //       }
  //     }
  //     this.subtypes = common_util.unique_array(this.subtypes);
  //     this.class_ids = obj.class_ids ? obj.class_ids : [];
  //     this.challenge_rating = obj.challenge_rating ? obj.challenge_rating : 0;
  //     this.min_challenge_rating = obj.min_challenge_rating ? obj.min_challenge_rating : this.challenge_rating;
  //     this.max_challenge_rating = obj.max_challenge_rating ? obj.max_challenge_rating : this.challenge_rating;
  //     this.name_table = obj.name_table ? obj.name_table : [];

  //     this.ability_scores = obj.ability_scores ? new AbilityScoresUpgradable(obj.ability_scores) : new AbilityScoresUpgradable(8);
      
  //     this.resources_table = obj.resources_table ? new AttachedTable(obj.resources_table) : new AttachedTable();
      
  //     // if (this.name.includes("Ancient Gold Dragon")) {
  //     //   debugger;
  //     // }
  //     this.legendaries_table = obj.legendaries_table ? new LegendariesTable(obj.legendaries_table) : new LegendariesTable();
      
  //     if (obj.fbs) {
  //       this.fbs = obj.fbs.map((o: any) => new FeatureBase(o));
  //     } else {
  //       if (obj.action_fbs) {
  //         let fbs: FeatureBase[] = obj.action_fbs.map((o: any) => new FeatureBase(o));
  //         fbs = fbs.filter(o => o.features.length > 0);
  //         for (const fb of fbs) {
  //           fb.creature_fb_type = "Actions";
  //         }
  //         this.fbs.push(...fbs);
  //       } 
        
  //       if (obj.legendary_action_fbs) {
  //         let fbs: FeatureBase[] = obj.legendary_action_fbs.map((o: any) => new FeatureBase(o));
  //         fbs = fbs.filter(o => o.features.length > 0);
  //         for (const fb of fbs) {
  //           fb.creature_fb_type = "Legendary Actions";
  //         }
  //         this.fbs.push(...fbs);
  //       } 
  //       if (obj.lair_action_fbs) {
  //         let fbs: FeatureBase[] = obj.lair_action_fbs.map((o: any) => new FeatureBase(o));
  //         fbs = fbs.filter(o => o.features.length > 0);
  //         for (const fb of fbs) {
  //           fb.creature_fb_type = "Lair Actions";
  //         }
  //         this.fbs.push(...fbs);
  //       } 
  //       if (obj.traits) {
  //         let fbs: FeatureBase[] = obj.traits.map((o: any) => new FeatureBase(o));
  //         fbs = fbs.filter(o => o.features.length > 0);
  //         for (const fb of fbs) {
  //           fb.creature_fb_type = "Traits";
  //         }
  //         this.fbs.push(...fbs);
  //       } 
  //     }
      
  //     this.spellcasting_table = obj.spellcasting_table ? new SpellcastingTable(obj.spellcasting_table) : new SpellcastingTable();
      
  //     this.hit_points_called = obj.hit_points_called ? obj.hit_points_called : "";
  //     this.hit_dice = []; 
  //     if (obj.hit_dice) {
  //       if (obj.hit_dice.length) {
  //         obj.hit_dice.forEach((hd: any) => {
  //           this.hit_dice.push(new HitDiceUpgradable(hd));
  //         });
  //       } else {
  //         this.hit_dice.push(new HitDiceUpgradable(obj.hit_dice));
  //       }
  //     }
  //     // Default to CON
  //     this.hit_dice_bonus = obj.hit_dice_bonus ? new UpgradableNumber(obj.hit_dice_bonus) : new UpgradableNumber({ add_ability_scores: ["CON"], add_ability_mod_mult: 1 });
      
  //     this.extra_hp_pools = [];
  //     if (obj.extra_hp_pools) {
  //       for (const o of obj.extra_hp_pools) {
  //         this.extra_hp_pools.push({ true_id: o.true_id, called: o.called, max_hp: new UpgradableNumber(o.max_hp) });
  //       }
  //     }
  //     this.finishing_blow = obj.finishing_blow ? new UpgradableNumber(obj.finishing_blow) : new UpgradableNumber();
  //     this.starting_hp = obj.starting_hp ? new UpgradableNumber(obj.starting_hp) : null;

  //     this.initiative_modifier = obj.initiative_modifier ? new UpgradableNumber(obj.initiative_modifier) : new UpgradableNumber();
  //     this.armor_class = obj.armor_class ? new UpgradableNumber(obj.armor_class) : new UpgradableNumber();
  //     if (this.armor_class.isBlank()) {
  //       this.armor_class.add_ability_mod_mult = 1;
  //       this.armor_class.add_ability_scores.push("DEX");
  //       this.armor_class.base = 10;
  //     }
  //     this.ac_flavor = obj.ac_flavor ? obj.ac_flavor : "";
  //     this.size_ugn = obj.size_ugn ? new UpgradableNumber(obj.size_ugn) : new UpgradableNumber();
  //     if (this.size_ugn.isBlank()) {
  //       if (obj.size_num) {
  //         this.size_ugn.base = obj.size_num;
  //       } else if (obj.size) {
  //         this.size_ugn.base = SIZE_TO_NUM_MAP[obj.size];
  //       } else {
  //         this.size_ugn.base = 3;
  //       }
  //     }
  //     this.alignment = obj.alignment ? common_util.capitalize_firsts(obj.alignment) : "Neutral";

  //     this.environments = obj.environments ? obj.environments : [];
      
  //     if (obj.settings && obj.settings.length > 0 && this.world_settings.length === 0) {
  //       this.world_settings = obj.settings as WorldSetting[];
  //     }
  //     if (this.world_settings.includes("Any")) {
  //       this.world_settings = this.world_settings.filter(o => o !== "Any");
  //     }
  //     this.native_planes = obj.native_planes ? obj.native_planes : [];

  //     this.group_id = obj.group_id ? obj.group_id : "";
  //     this.gear_item_ids = obj.gear_item_ids ? obj.gear_item_ids : [];

  //     this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "";
  //     this.dc = obj.dc ? new UpgradableNumber(obj.dc) : new UpgradableNumber();
  //     if (this.dc.isBasic() && this.dc.base === -1) {
  //       this.dc.base = 0;
  //     }
  //     this.magic_attack = obj.magic_attack ? new UpgradableNumber(obj.magic_attack) : new UpgradableNumber();
  //     if (this.magic_attack.isBasic() && this.magic_attack.base === -1) {
  //       this.magic_attack.base = 0;
  //     }
  //     this.non_magic_attack = obj.non_magic_attack ? new UpgradableNumber(obj.non_magic_attack) : new UpgradableNumber();
  //     if (this.non_magic_attack.isBasic() && this.non_magic_attack.base === -1) {
  //       this.non_magic_attack.base = 0;
  //     }
  //     this.magic_dmg_bonus = obj.magic_dmg_bonus ? new UpgradableNumber(obj.magic_dmg_bonus) : new UpgradableNumber();
  //     if (this.magic_dmg_bonus.isBasic() && this.magic_dmg_bonus.base === -1) {
  //       this.magic_dmg_bonus.base = 0;
  //     }
  //     this.non_magic_dmg_bonus = obj.non_magic_dmg_bonus ? new UpgradableNumber(obj.non_magic_dmg_bonus) : new UpgradableNumber();
  //     if (this.non_magic_dmg_bonus.isBasic() && this.non_magic_dmg_bonus.base === -1) {
  //       this.non_magic_dmg_bonus.base = 0;
  //     }
      
  //     this.string_hash = obj.string_hash ? obj.string_hash : {};
  //     this.upgradable_hash = {};
  //     if (obj.upgradable_hash) {
  //       Object.keys(obj.upgradable_hash).forEach(key => {
  //         this.upgradable_hash[key] = new UpgradableNumber(obj.upgradable_hash[key]);
  //       });
  //     }
      
  //     this.simple_character = obj.simple_character ? obj.simple_character : false;

  //     this.table = obj.table ? new AttachedTable(obj.table) : new AttachedTable();
  //     this.attributes_table = obj.attributes_table ? new AttributesTable(obj.attributes_table) : new AttributesTable();
  //     if (!obj.attributes_table) {
  //       const entry = new Attributes();
  //       entry.level = this.min_challenge_rating;
  //       entry.speeds = obj.speed ? obj.speed : {
  //         walk: 0,
  //         swim: 0,
  //         climb: 0,
  //         fly: 0,
  //         burrow: 0,
  //         hover: 0
  //       };

  //       entry.save_profs = [];
  //       const saves = obj.saving_throws2 ? obj.saving_throws2 : [];
  //       if (typeof(saves) === "object") {
  //         if (saves.length) {
  //           entry.save_profs = saves;
  //         } else {
  //           entry.save_profs = Object.keys(saves);
  //         }
  //       }

  //       entry.languages = obj.languages ? obj.languages : "";
  //       entry.tool_profs = obj.tool_profs ? obj.tool_profs : []; 
  //       if (!obj.tool_profs && obj.tool_proficiencies) {
  //         const tool_proficiencies = obj.tool_proficiencies;
  //         const keys = Object.keys(tool_proficiencies);
  //         entry.tool_profs = keys;
  //       }

  //       entry.condition_immunities = [];
  //       if (obj.condition_immunities && obj.condition_immunities.length) {
  //         obj.condition_immunities.forEach((ci: any) => {
  //           // This is to fix an issue that happened when I was initially copying and parsing these
  //           if (typeof(ci) === "string") {
  //             entry.condition_immunities.push(ci);
  //           }
  //         });
  //       }
  //       entry.extra_damage_immunities = obj.extra_damage_immunities && typeof(obj.extra_damage_immunities) === "string" ? obj.extra_damage_immunities : "";
  //       entry.damage_immunities = [];
  //       if (obj.damage_immunities && obj.damage_immunities.length > 0) {
  //         entry.damage_immunities = obj.damage_immunities;
  //       } else if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
  //         obj.damage_multipliers.filter((o: any) => o.multiplier === 0).forEach((dm: any) => {
  //           entry.damage_immunities = [...entry.damage_immunities,...dm.damage_types];
  //         });
  //       }
  //       entry.damage_immunities = entry.damage_immunities.map(o => {
  //         return o[0].toLocaleUpperCase() + o.substring(1);
  //       });
  //       if (entry.extra_damage_immunities.length === 0) {
  //         let non_fitting = entry.damage_immunities.filter(o => !DAMAGE_TYPES.includes(o));
  //         entry.damage_immunities = entry.damage_immunities.filter(o => DAMAGE_TYPES.includes(o));
  //         if (non_fitting.length > 0) {
  //           if (entry.extra_damage_immunities.length > 0) {
  //             entry.extra_damage_immunities += ", ";
  //           }
  //           entry.extra_damage_immunities += non_fitting.join(", ");
  //         }
  //       } else {
  //         entry.damage_immunities = entry.damage_immunities.filter(o => DAMAGE_TYPES.includes(o));
  //       }
  //       entry.extra_damage_resistances = obj.extra_damage_resistances && typeof(obj.extra_damage_resistances) === "string" ? obj.extra_damage_resistances : "";
  //       entry.damage_resistances = [];
  //       if (obj.damage_resistances && obj.damage_resistances.length > 0) {
  //         entry.damage_resistances = obj.damage_resistances;
  //       } else if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
  //         obj.damage_multipliers.filter((o: any) => o.multiplier === 0.5).forEach((dm: any) => {
  //           entry.damage_resistances = [...entry.damage_resistances,...dm.damage_types];
  //         });
  //       }
  //       entry.damage_resistances = entry.damage_resistances.map(o => {
  //         return o[0].toLocaleUpperCase() + o.substring(1);
  //       });
  //       if (entry.extra_damage_resistances.length === 0) {
  //         let non_fitting = entry.damage_resistances.filter(o => !DAMAGE_TYPES.includes(o));
  //         entry.damage_resistances = entry.damage_resistances.filter(o => DAMAGE_TYPES.includes(o));
  //         if (non_fitting.length > 0) {
  //           if (entry.extra_damage_resistances.length > 0) {
  //             entry.extra_damage_resistances += ", ";
  //           }
  //           entry.extra_damage_resistances += non_fitting.join(", ");
  //         }
  //       } else {
  //         entry.damage_resistances = entry.damage_resistances.filter(o => DAMAGE_TYPES.includes(o));
  //       }
  //       entry.extra_damage_vulnerabilities = obj.extra_damage_vulnerabilities && typeof(obj.extra_damage_vulnerabilities) === "string" ? obj.extra_damage_vulnerabilities : "";
  //       entry.damage_vulnerabilities = [];
  //       if (obj.damage_vulnerabilities && obj.damage_vulnerabilities.length > 0) {
  //         entry.damage_vulnerabilities = obj.damage_vulnerabilities;
  //       } else if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
  //         obj.damage_multipliers.filter((o: any) => o.multiplier === 2).forEach((dm: any) => {
  //           entry.damage_vulnerabilities = [...entry.damage_vulnerabilities,...dm.damage_types];
  //         });
  //       }
  //       entry.damage_vulnerabilities = entry.damage_vulnerabilities.map(o => {
  //         return o[0].toLocaleUpperCase() + o.substring(1);
  //       });
  //       if (entry.extra_damage_vulnerabilities.length === 0) {
  //         let non_fitting = entry.damage_vulnerabilities.filter(o => !DAMAGE_TYPES.includes(o));
  //         entry.damage_vulnerabilities = entry.damage_vulnerabilities.filter(o => DAMAGE_TYPES.includes(o));
  //         if (non_fitting.length > 0) {
  //           if (entry.extra_damage_vulnerabilities.length > 0) {
  //             entry.extra_damage_vulnerabilities += ", ";
  //           }
  //           entry.extra_damage_vulnerabilities += non_fitting.join(", ");
  //         }
  //       } else {
  //         entry.damage_vulnerabilities = entry.damage_vulnerabilities.filter(o => DAMAGE_TYPES.includes(o));
  //       }
  //       // if (this.name.includes("Vine Dragon Wy")) {
  //       //   debugger;
  //       // }
  //       entry.senses = {};
  //       if (obj.senses) {
  //         if (obj.senses.length > 0) {
  //           obj.senses.forEach((sense: any) => {
  //             console.log(sense);
  //             // entry.senses[sense.name] = sense.range;
  //             // entry.senses.push(new CharacterSense(sense));
  //           });
  //         } else {
  //           entry.senses = obj.senses;
  //         }
  //       }
  //       entry.skill_profs = obj.skill_proficiencies2 ? obj.skill_proficiencies2 : {};
  //       if (!obj.skill_proficiencies2 && obj.skill_proficiencies) {
  //         const skill_ids = Object.keys(obj.skill_proficiencies);
  //         const proficiency_modifier = this.proficiency_modifier;
  //         const table_entry = this.table.entries.find(o => o.level === this.challenge_rating);
  //         for (const id of skill_ids) {
  //           let skill_id = id;
  //           let skill = SKILLS.find(o => o._id === skill_id);
  //           if (!skill) {
  //             skill = SKILLS.find(o => o.name === skill_id);
  //             if (skill) {
  //               skill_id = skill._id;
  //             }
  //           }
  //           const old_value = obj.skill_proficiencies[id];
  //           if (skill && old_value) {
  //             const ability_score = this.ability_scores.getAbilityScore(skill.use_ability_score);
  //             if (ability_score) {
  //               let score = ability_score.base;
  //               if (!ability_score.isBasic()) {
  //                 if (ability_score.table_type === "Attached" && table_entry) {
  //                   score = table_entry.data[skill.use_ability_score];
  //                 }
  //               }
  //               const mod = Math.floor((score - 10) / 2);
  //               if (old_value === mod + proficiency_modifier) {
  //                 entry.skill_profs[skill_id] = 1;
  //               } else if (old_value === mod + proficiency_modifier * 2) {
  //                 entry.skill_profs[skill_id] = 2;
  //               } else if (old_value === mod + proficiency_modifier * 0.5) {
  //                 entry.skill_profs[skill_id] = 0.5;
  //               }
  //             }
  //           }
  //         }
  //         // if (this.name === "Mind Flayer") {
  //         //   console.log(this.skill_proficiencies2);
  //         // }
  //       }
  //       entry.skill_profs = obj.skill_proficiencies2 ? obj.skill_proficiencies2 : {};
  //       if (!obj.skill_proficiencies2 && obj.skill_proficiencies) {
  //         const skill_ids = Object.keys(obj.skill_proficiencies);
  //         const proficiency_modifier = this.proficiency_modifier;
  //         const table_entry = this.table.entries.find(o => o.level === this.challenge_rating);
  //         for (const id of skill_ids) {
  //           let skill_id = id;
  //           let skill = SKILLS.find(o => o._id === skill_id);
  //           if (!skill) {
  //             skill = SKILLS.find(o => o.name === skill_id);
  //             if (skill) {
  //               skill_id = skill._id;
  //             }
  //           }
  //           const old_value = obj.skill_proficiencies[id];
  //           if (skill && old_value) {
  //             const ability_score = this.ability_scores.getAbilityScore(skill.use_ability_score);
  //             if (ability_score) {
  //               let score = ability_score.base;
  //               if (!ability_score.isBasic()) {
  //                 if (ability_score.table_type === "Attached" && table_entry) {
  //                   score = table_entry.data[skill.use_ability_score];
  //                 }
  //               }
  //               const mod = Math.floor((score - 10) / 2);
  //               if (old_value === mod + proficiency_modifier) {
  //                 entry.skill_profs[skill_id] = 1;
  //               } else if (old_value === mod + proficiency_modifier * 2) {
  //                 entry.skill_profs[skill_id] = 2;
  //               } else if (old_value === mod + proficiency_modifier * 0.5) {
  //                 entry.skill_profs[skill_id] = 0.5;
  //               }
  //             }
  //           }
  //         }
  //         // if (this.name === "Mind Flayer") {
  //         //   console.log(this.skill_proficiencies2);
  //         // }
  //       }
  //       this.attributes_table.entries.push(entry);
  //     }
  //     // Identify and merge duplicate rows (same level)
  //     const attribute_levels = common_util.unique_array(this.attributes_table.entries.map(o => o.level));
  //     if (attribute_levels.length !== this.attributes_table.entries.length) {
  //       // We've got duplicates.
  //       for (const level of attribute_levels) {
  //         const entries_of_level = this.attributes_table.entries.filter(o => o.level === level);
  //         if (entries_of_level.length > 1) {
  //           const prime_entry = entries_of_level[0];
  //           const delete_us: string[] = [];
  //           for (let i = 1; i < entries_of_level.length; ++i) {
  //             const delete_me = entries_of_level[i];
  //             prime_entry.merge(delete_me);
  //             delete_us.push(delete_me.true_id);
  //           }
  //           this.attributes_table.entries = this.attributes_table.entries.filter(o => !delete_us.includes(o.true_id));
  //         }
  //       }
  //     }
  //     this.small_name = obj.small_name ? obj.small_name : "";
  //     if (this.small_name === "") {
  //       switch (this.creature_type) {
  //         case "Humanoid": break;
  //         case "Beast": break;
  //         case "Construct": break;
  //         case "Aberration": break;
  //         // case "Undead": break;
  //         // case "Dragon": break;
  //         case "Monstrosity": break;
  //         // case "Elemental": break;
  //         // case "Fey": break;
  //         // case "Giant": break;
  //         // case "Ooze": break;
  //         // case "Plant": break;
  //         case "Shapechanger": break;
  //         // case "Celestial": break;
  //         // case "Fiend": break;
  //         default: this.small_name = this.creature_type.toLocaleLowerCase(); break;
  //       }
  //     }
  //     if (this.small_name === "") {
  //       if (this.name.includes(" ")) {
  //         const pieces = this.name.split(" ");
  //         if (pieces.length === 2) {
  //           this.small_name = pieces[1].toLocaleLowerCase();
  //         }
  //       }
  //       if (this.small_name.length === 0) {
  //         this.small_name = this.name.toLocaleLowerCase();
  //       }
  //     }
  //     this.flavor_text = obj.flavor_text ? obj.flavor_text : "";
  //   }
  // }

  // get_creature_homebrewery_string(api: DandDAPIClass, char_util: DandDCharacterUtilitiesClass, ci: CreatureInstance | undefined = undefined) {
  //   let str = `# ${ci ? ci.name : this.true_name}\n\r  `;
  //   let ac = "See Properties Table";
  //   let hp = "See Properties Table";
  //   let saving_throws = "";
  //   let skills = "";
  //   let senses_string = "";
  //   let tools = "";
  //   let immunities_string = "";
  //   let resistances_string = "";
  //   let vulnerabilities_string = "";
  //   let condition_immunities_string = "";
  //   let languages = "";
  //   let cr_string = "";
  //   let prof_bonus = "See Properties Table";
  //   let table_string = "";
  //   let resource_table_string = "";
  //   let x_day_table_string = "";
  //   let attributes_table_string = "";
  //   let size = ci ? `${ci.size} ` : this.size_ugn.isBasic() ? `${SIZE_MAP[this.size_ugn.base]} ` : "";
  //   let one_resources_entry: TableEntry | null = null;
  //   let one_x_day_entry: TableEntry | null = null;
  //   if (ci) {
  //     ac = `${ci.armor_class}`;
  //     hp = `${this.hit_dice_string_for_cr(ci.challenge_rating)}`;
  //     saving_throws = ci.saving_throws_string;
  //     skills = ci.skills_string;
  //     senses_string = ci.senses_string;
  //     tools = ci.tools_string;
  //     cr_string = `<b>Challenge</b> ${ci.challenge_rating} (${common_util.cr_to_xp_map[ci.challenge_rating]} XP)&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;`;
  //     prof_bonus = `+${ci.proficiency_modifier}`;
  //     table_string = `<table><tr><th>STR</th><th>DEX</th><th>CON</th></tr>
  //       <tr>
  //         <td>${ci.current_ability_scores.strength} (${common_util.addPlusMaybe(ci.current_ability_scores.STR)})</td>
  //         <td>${ci.current_ability_scores.dexterity} (${common_util.addPlusMaybe(ci.current_ability_scores.DEX)})</td>
  //         <td>${ci.current_ability_scores.constitution} (${common_util.addPlusMaybe(ci.current_ability_scores.CON)})</td>
  //       </tr>
  //       <tr><th>INT</th><th>WIS</th><th>CHA</th></tr>
  //       <tr>
  //         <td>${ci.current_ability_scores.intelligence} (${common_util.addPlusMaybe(ci.current_ability_scores.INT)})</td>
  //         <td>${ci.current_ability_scores.wisdom} (${common_util.addPlusMaybe(ci.current_ability_scores.WIS)})</td>
  //         <td>${ci.current_ability_scores.charisma} (${common_util.addPlusMaybe(ci.current_ability_scores.CHA)})</td>
  //       </tr>
  //       </table>`;
  //   } else {
  //     const attributes = this.attributes_table.entries[0];
  //     if (this.attributes_table.entries.length === 1) {
  //       saving_throws = attributes.saving_throws_string;
  //       skills = attributes.skills_string;
  //       tools = attributes.tools_string;
  //       immunities_string = attributes.immunities_string;
  //       resistances_string = attributes.resistances_string;
  //       vulnerabilities_string = attributes.vulnerabilities_string;
  //       condition_immunities_string = attributes.condition_immunities_string;
  //       languages = attributes.languages;
  //       senses_string = attributes.senses_string;
  //     } else if (attributes) {
  //       const unchanged: string[] = [];
  //       const attributes_table = this.attributes_table;
  //       if (attributes_table.save_profs_unchanged) {
  //         unchanged.push("save_profs");
  //         saving_throws = attributes.saving_throws_string;
  //       } else {
  //         saving_throws = "See Attributes Table";
  //       }
  //       if (attributes_table.skill_profs_unchanged) {
  //         unchanged.push("skill_profs");
  //         skills = attributes.skills_string;
  //       } else {
  //         skills = "See Attributes Table";
  //       }
  //       if (attributes_table.tool_profs_unchanged) {
  //         unchanged.push("tool_profs");
  //         tools = attributes.tools_string;
  //       } else {
  //         tools = "See Attributes Table";
  //       }
  //       if (attributes_table.damage_immunities_unchanged) {
  //         unchanged.push("damage_immunities");
  //         immunities_string = attributes.immunities_string;
  //       } else {
  //         immunities_string = "See Attributes Table";
  //       }
  //       if (attributes_table.damage_resistances_unchanged) {
  //         unchanged.push("damage_resistances");
  //         resistances_string = attributes.resistances_string;
  //       } else {
  //         resistances_string = "See Attributes Table";
  //       }
  //       if (attributes_table.damage_vulnerabilities_unchanged) {
  //         unchanged.push("damage_vulnerabilities");
  //         vulnerabilities_string = attributes.vulnerabilities_string;
  //       } else {
  //         vulnerabilities_string = "See Attributes Table";
  //       }
  //       if (attributes_table.condition_immunities_unchanged) {
  //         unchanged.push("condition_immunities");
  //         condition_immunities_string = attributes.condition_immunities_string;
  //       } else {
  //         condition_immunities_string = "See Attributes Table";
  //       }
  //       if (attributes_table.languages_unchanged) {
  //         unchanged.push("languages");
  //         languages = attributes.languages;
  //       } else {
  //         languages = "See Attributes Table";
  //       }
  //       if (attributes_table.senses_unchanged) {
  //         unchanged.push("senses");
  //         senses_string = attributes.senses_string;
  //       } else {
  //         senses_string = "See Attributes Table";
  //       }
        
  //       const attribute_fields = attributes_table.all_attributes.filter(o => !unchanged.includes(o));
  //       if (attribute_fields.length > 0) {
  //         // Show the table.
  //         const attribute_map: { [key: string]: string } = {
  //           "damage_immunities": "Immunities",
  //           "damage_resistances": "Resistances",
  //           "damage_vulnerabilities": "Vulnerabilities",
  //           "condition_immunities": "Condition Immunities",
  //           "senses": "Senses",
  //           "skill_profs": "Skills",
  //           "save_profs": "Saves",
  //           "speeds": "Speeds",
  //           "tool_profs": "Tools",
  //           "languages": "Languages"
  //         };
  //         attributes_table_string += "<b>Attributes by CR</b>\n\r  ";
  //         const add_attributes_table = (using_fields: string[]) => {
  //           attributes_table_string += "<table>\n\r";
  //           attributes_table_string += `<tr><th>CR</th>${using_fields.map(o => { 
  //             return "<th>" + attribute_map[o] + "</th>"; 
  //           }).join("")}</tr>\n\r`;
  //           for (const entry of attributes_table.entries.sort((a, b) => { return a.level - b.level; })) {
  //             attributes_table_string += "<tr>";
  //             attributes_table_string += "<td>" + entry.level + "</td>";
  //             attributes_table_string += using_fields.map(field => { 
  //               const data = (entry as any)[field];
  //               let str = "";
  //               if (typeof data === "object") {
  //                 if (data.length) {
  //                   str = data.map((o: any) => `${o}`).join(", ");
  //                 } else {
  //                   const keys = Object.keys(data);
  //                   if (field === "skill_profs") {
  //                     str = keys.map(key => {
  //                       const skill = char_util.skills.find(o => o._id === key);
  //                       const prof = +data[key];
  //                       if (skill) {
  //                         return `${skill.name}${prof > 1 ? " (Expert)" : ""}`;
  //                       }
  //                       return "";
  //                     }).filter(o => o !== "").join("\n\r  ");
  //                   } else {
  //                     str = keys.map(key => {
  //                       return `${key}: ${data[key]}`;
  //                     }).join("\n\r  ");
  //                   }
  //                 }
  //               } else {
  //                 str = `${data}`;
  //               }
  //               return "<td>" + str + "</td>"; 
  //             }).join("");
  //             attributes_table_string += "</tr>\n\r";
  //           }
  //           attributes_table_string += "</table>\n\r  ";
  //         }
  //         switch (attribute_fields.length) {
  //           case 6: 
  //             if (true) {
  //               const fields1 = attribute_fields.slice(0, 2);
  //               const fields2 = attribute_fields.slice(3);
  //               add_attributes_table(fields1); 
  //               attributes_table_string += "<b>More Attributes by CR</b>\n\r  ";
  //               add_attributes_table(fields2); 
  //             }
  //           break;
  //           case 7: case 8:
  //             if (true) {
  //               const fields1 = attribute_fields.slice(0, 3);
  //               const fields2 = attribute_fields.slice(4);
  //               add_attributes_table(fields1); 
  //               attributes_table_string += "<b>More Attributes by CR</b>\n\r  ";
  //               add_attributes_table(fields2); 
  //             }
  //           break;
  //           case 9: case 10:
  //             if (true) {
  //               const fields1 = attribute_fields.slice(0, 2);
  //               const fields2 = attribute_fields.slice(3, 5);
  //               const fields3 = attribute_fields.slice(6);
  //               add_attributes_table(fields1); 
  //               attributes_table_string += "<b>More Attributes by CR</b>\n\r  ";
  //               add_attributes_table(fields2); 
  //               attributes_table_string += "<b>Even More Attributes by CR</b>\n\r  ";
  //               add_attributes_table(fields3); 
  //             }
  //           break;
  //           default: add_attributes_table(attribute_fields); break;
  //         }
  //       }
  //     }
  //     // Show the table.
  //     let table = this.table;

  //     if (table.entries.length > 0 && table.fields.length > 0) {
  //       let show_size = false;
  //       if (table.fields.includes("Size")) {
  //         const sizes = table.entries.map(o => o.data["Size"]);
  //         const min_size = Math.min(...sizes);
  //         const max_size = Math.max(...sizes);
  //         if (min_size === max_size) {
  //           size = SIZE_MAP[min_size];
  //         } else {
  //           show_size = true;
  //         }
  //       }
  //       const fields = table.fields.filter(o => o !== "Armor Class" && o !== "Size");
  //       table_string = "<b>Ability Scores by CR</b>\n\r  ";
  //       table_string += "<table>\n\r";
  //       table_string += `<tr><th>CR</th>${fields.map(o => { 
  //         if (o.length < 4) {
  //           return "<th>&nbsp;" + o + "&nbsp;</th>"; 
  //         } else {
  //           return "<th>" + o + "</th>"; 
  //         }
  //       }).join("")}<th>Max HP</th><th>AC</th>${show_size ? "<th>Size</th>" : ""}</tr>\n\r`;
  //       for (const entry of table.entries.filter(o => 
  //         o.level >= this.min_challenge_rating && 
  //         o.level <= this.max_challenge_rating).sort((a, b) => {
  //         return a.level - b.level;
  //       })) {
  //         console.log(entry);
  //         table_string += `<tr><td>${entry.level}</td>${fields.map(o => { return "<td>" + entry.data[o] + "</td>" }).join("")}</tr>\n\r`;
  //       }
  //       table_string += "</table>\n\r  ";
  //       table_string = "<b>Creature Properties by CR</b>\n\r  ";
  //       table_string += "<table>\n\r";
  //       table_string += `<tr><th>CR</th><th>Proficiency Bonus</th><th>Max HP</th><th>AC</th>${show_size ? "<th>Size</th>" : ""}</tr>\n\r`;
  //       let ac_of_others = 0;
  //       for (const entry of table.entries.filter(o => 
  //         o.level >= this.min_challenge_rating && 
  //         o.level <= this.max_challenge_rating).sort((a, b) => {
  //         return a.level - b.level;
  //       })) {
  //         console.log(entry);
  //         table_string += `<tr><td>${entry.level}</td>`;
  //         const ac_for_cr = this.ac_for_cr(entry.level);
  //         if (ac_of_others === 0) {
  //           ac_of_others = ac_for_cr;
  //         } else if (ac_of_others > 0 && ac_of_others !== ac_for_cr) {
  //           ac_of_others = -1;
  //         }
  //         table_string += `<td>${this.get_proficiency_modifier(entry.level)}</td><td>${this.max_hp_for_cr(entry.level)}</td><td>${ac_for_cr}</td>${show_size ? "<td>" + SIZE_MAP[entry.data["Size"]] + "</td>" : ""}</tr>\n\r`;
  //       }
  //       if (ac_of_others > 0) {
  //         ac = `${ac_of_others}`;
  //       }
  //       table_string += "</table>\n\r  ";
  //     } else {
  //       table_string += "TABLE NOT FOUND!\n\r  ";
  //     }

  //     table = this.resources_table;

  //     if (table.entries.length > 1 && table.fields.length > 0) {
  //       const fields = table.fields.filter(o => !o.endsWith("_size"));
  //       resource_table_string = "<b>Resources by CR</b>\n\r  ";
  //       resource_table_string += "<table>\n\r";
  //       resource_table_string += `<tr><th>CR</th>${fields.map(o => { 
  //         if (o.length < 4) {
  //           return "<th>&nbsp;" + o + "&nbsp;</th>"; 
  //         } else {
  //           return "<th>" + o + "</th>"; 
  //         }
  //       }).join("")}</tr>\n\r`;
  //       for (const entry of table.entries.filter(o => 
  //         o.level >= this.min_challenge_rating && 
  //         o.level <= this.max_challenge_rating).sort((a, b) => {
  //         return a.level - b.level;
  //       })) {
  //         resource_table_string += `<tr><td>${entry.level}</td>${fields.map(o => { return "<td>" + entry.data[o] + "</td>" }).join("")}</tr>\n\r`;
  //       }
  //       resource_table_string += "</table>\n\r  ";
  //     } else if (table.entries.length === 1) {
  //       one_resources_entry = table.entries[0];
  //     }

  //     table = this.x_day_table;

  //     if (table.entries.length > 1 && table.fields.length > 0) {
  //       const fields = table.fields.filter(o => !o.endsWith("_size"));
  //       x_day_table_string = "<b>Per Day Spells by CR</b>\n\r  ";
  //       x_day_table_string += "<table>\n\r";
  //       x_day_table_string += `<tr><th>CR</th>${fields.map(spell_id => { 
  //         const spell = char_util.spells.find(o => o._id === spell_id);
  //         if (spell) {
  //           return "<th>" + spell.name + "</th>"; 
  //         }
  //         return ""; 
  //       }).join("")}</tr>\n\r`;
  //       for (const entry of table.entries.filter(o => 
  //         o.level >= this.min_challenge_rating && 
  //         o.level <= this.max_challenge_rating).sort((a, b) => {
  //         return a.level - b.level;
  //       })) {
  //         x_day_table_string += `<tr><td>${entry.level}</td>${fields.map(o => { return "<td>" + entry.data[o] + "</td>" }).join("")}</tr>\n\r`;
  //       }
  //       x_day_table_string += "</table>\n\r  ";
  //     } else if (table.entries.length === 1) {
  //       one_x_day_entry = table.entries[0];
  //     }
  //   }
  //   str += `<style>
  //     div.hr {
  //       width: 0; 
  //       height: 0; 
  //       border-top: 5px solid transparent;
  //       border-bottom: 5px solid transparent;
  //       border-left: 300px solid brown;
  //       }
  //     td {
  //       justify-content: center;
  //       text-align: center;
  //       align-items: center;
  //     }
  //     th {
  //       font-weight: bold
  //     }
  //     b {
  //       font-weight: bold
  //     }
  //     </style>`;
  //   str += `${size}${this.creature_type}${this.alignment === "" ? "" : ", " + this.alignment}\n\r  `;
  //   str += `<div class="hr"></div>\n\r  `;
  //   str += `<b>Armor Class</b> ${ac} ${this.ac_flavor.length > 0 ? "(" + this.ac_flavor + ")" : ""}\n\r  `;
  //   str += `<b>Hit Points</b> ${hp}\n\r  `;
  //   str += `<b>Speed</b> ${this.speed_string}\n\r  `;
  //   str += `<div class="hr"></div>\n\r  `;
  //   str += table_string;
  //   str += resource_table_string;
  //   str += x_day_table_string;
  //   str += attributes_table_string;
  //   str += `<div class="hr"></div>\n\r  `;
  //   if (saving_throws.length > 0) {
  //     str += `<b>Saving Throws</b> ${saving_throws}\n\r  `;
  //   }
  //   if (skills.length > 0) {
  //     str += `<b>Skills</b> ${skills}\n\r  `;
  //   }
  //   if (tools.length > 0) {
  //     str += `<b>Tools</b> ${tools}\n\r  `;
  //   }
  //   if (immunities_string.length > 0) {
  //     str += `<b>Damage Immunities</b> ${immunities_string}\n\r  `;
  //   }
  //   if (resistances_string.length > 0) {
  //     str += `<b>Damage Resistances</b> ${resistances_string}\n\r  `;
  //   }
  //   if (vulnerabilities_string.length > 0) {
  //     str += `<b>Damage Vulnerabilities</b> ${vulnerabilities_string}\n\r  `;
  //   }
  //   if (condition_immunities_string.length > 0) {
  //     str += `<b>Damage Immunities</b> ${condition_immunities_string}\n\r  `;
  //   }
  //   str += `<b>Senses</b> ${senses_string}\n\r  `;
  //   str += `<b>Languages</b> ${languages}\n\r  `;
  //   str += `${cr_string}<b>Proficiency Bonus</b> ${prof_bonus}\n\r  `;
  //   str += `<div class="hr"></div>\n\r  `;
  //   str += this.get_homebrewery_fbs_string(api, char_util, ci, one_resources_entry, one_x_day_entry);
  //   str += `${this.description}\n\r  `;
  //   return str;
  // }

  // get_homebrewery_fbs_string(api: DandDAPIClass, char_util: DandDCharacterUtilitiesClass, ci: CreatureInstance | undefined, one_resources_entry: TableEntry | null, one_x_day_entry: TableEntry | null): string {
  //   let str = "";
  //   let fbs = ci ? this.traits.filter(o => o.level <= ci.challenge_rating) : this.traits;
  //   if (fbs.length > 0 || this.legendaries_table.entries.length > 0) {
  //     str += "## Traits\n\r  ";
  //     if (ci) {
  //       for (const fb of fbs) {
  //         str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: this.spellcasting_ability !== "None" ? this.spellcasting_ability : undefined })}\n\r  `;
  //       }
  //     } else {
  //       const crs = (common_util.unique_array(fbs.map(o => o.level)) as number[]).sort();
  //       if (crs.length === 1 && crs[0] <= this.min_challenge_rating) {
  //         for (const fb of fbs) {
  //           str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: this.spellcasting_ability !== "None" ? this.spellcasting_ability : undefined })}\n\r  `;
  //         }
  //       } else if (crs.length > 0) {
  //         for (const cr of crs) {
  //           str += `### At CR ${cr}\n\r  `;
  //           for (const fb of fbs.filter(o => o.level === cr)) {
  //             str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: this.spellcasting_ability !== "None" ? this.spellcasting_ability : undefined })}\n\r  `;
  //           }
  //         }
  //       }
  //     }
  //     if (this.legendaries_table.entries.length > 0) {
  //       let legendary_resistances = ""; 
  //       if (this.legendaries_table.entries.length === 1) {
  //         legendary_resistances = `${this.legendaries_table.entries[0].resistances}`;
  //       } else if (ci) {
  //         legendary_resistances = `${this.legendary_resistances_for_cr(ci.challenge_rating)}`;
  //       } else {
  //         legendary_resistances = "(found in the Resources table)";
  //       }
  //       str += `<b>Legendary Resistance (${legendary_resistances}/Day).</b> If ${this.small_name === "" ? this.name : "the " + this.small_name} fails a saving throw, it can choose to succeed instead.\n\r  `;
  //     }
  //   }
  //   fbs = ci ? this.action_fbs.filter(o => o.level <= ci.challenge_rating) : this.action_fbs;
  //   if (fbs.length > 0 || this.spellcasting_description.length > 0) {
  //     str += "## Actions\n\r  ";
  //     if (ci) {
  //       for (const fb of fbs) {
  //         str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: this.spellcasting_ability !== "None" ? this.spellcasting_ability : undefined })}\n\r  `;
  //       }
  //     } else {
  //       const crs = (common_util.unique_array(fbs.map(o => o.level)) as number[]).sort();
  //       if (crs.length === 1 && crs[0] <= this.min_challenge_rating) {
  //         for (const fb of fbs) {
  //           str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: this.spellcasting_ability !== "None" ? this.spellcasting_ability : undefined })}\n\r  `;
  //         }
  //       } else if (crs.length > 0) {
  //         for (const cr of crs) {
  //           str += `### At CR ${cr}\n\r  `;
  //           for (const fb of fbs.filter(o => o.level === cr)) {
  //             str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: this.spellcasting_ability !== "None" ? this.spellcasting_ability : undefined })}\n\r  `;
  //           }
  //         }
  //       }
  //     }
  //     if (this.spellcasting_description.length > 0) {
  //       str += `<b>Spellcasting.</b> ${char_util.getStringWithReplacements2({ clean_me: this.spellcasting_description, obj: ci, spellcasting_ability: this.spellcasting_ability !== "None" ? this.spellcasting_ability : undefined })}\n\r  `;
  //       const x_day_entry = ci ? this.x_day_table.getEntry(ci.challenge_rating) : one_x_day_entry;
  //       const resources_entry = ci ? this.resources_table.getEntry(ci.challenge_rating) : one_resources_entry;
  //       for (const level of Object.keys(this.spells).map(o => +o).sort()) {
  //         const spells_of_level = this.spells[level];
  //         if (spells_of_level.length > 0) {
  //           if (level === 0) {
  //             let level_string = "At will: ";
  //             for (const spell_of_level of spells_of_level) {
  //               const spell_id = spell_of_level.spell_id;
  //               const spell = (api.smart_hash.spell as Spell[]).find(o => o._id === spell_id);
  //               if (spell) {
  //                 if (level_string.length > 9) {
  //                   level_string += ", ";
  //                 }
  //                 level_string += spell.name;
  //               }
  //             }
  //             str += level_string + "\n\r  ";
  //           } else {
  //             const cast_types = common_util.unique_array(spells_of_level.map(o => o.cast_type));
  //             for (const cast_type of cast_types) {
  //               let level_string = "";
  //               switch (cast_type) {
  //                 case "Slot": 
  //                   // Check how many slots of this level they have
  //                   let level_name = "";
  //                   switch(level) {
  //                     case 1: level_name = "1st"; break;
  //                     case 2: level_name = "2nd"; break;
  //                     case 3: level_name = "3rd"; break;
  //                     default: level_name = `${level}th`; break;
  //                   }
  //                   const slots_of_level = resources_entry && Object.keys(resources_entry.data).includes(`Slot: ${level}`) ? resources_entry.data[ `Slot: ${level}`] : 0;
  //                   if (slots_of_level) {
  //                     level_string += `${level_name} level (${slots_of_level}):`;
  //                   } else if (!resources_entry) {
  //                     level_string += `${level_name} level (found in the Resources table):`;
  //                   }
  //                 break;
  //                 case "At Will":
  //                   level_string = "At will: ";
  //                 break;
  //                 case "Ritual": 
  //                   level_string = "As a Ritual: ";
  //                 break;
  //                 default:
  //                   if (x_day_entry) {
  //                     // Check if they're all the same.  They usually are.
  //                     const x_day = x_day_entry.data[spells_of_level[0].spell_id];
  //                     if (x_day) {
  //                       let matches = true;
  //                       for (const spell_of_level of spells_of_level.filter(o => o.cast_type === "X/Day")) {
  //                         if (x_day_entry.data[spell_of_level.spell_id] !== x_day) {
  //                           matches = false;
  //                           break;
  //                         }
  //                       }
  //                       if (matches) {
  //                         level_string = `${x_day}/Day each: `;
  //                       }
  //                     } else {
  //                       console.error("x_day missing");
  //                     }
  //                   } else {
  //                     level_string = `A number of times per day found in the Per Day Spells Table: `;
  //                   }
  //                 break;
  //               }
  //               if (level_string.endsWith(": ")) {
  //                 for (const spell_of_level of spells_of_level.filter(o => o.cast_type === cast_type)) {
  //                   const spell_id = spell_of_level.spell_id;
  //                   const spell = (api.smart_hash.spell as Spell[]).find(o => o._id === spell_id);
  //                   if (spell) {
  //                     if (!level_string.endsWith(": ")) {
  //                       level_string += ", ";
  //                     }
  //                     level_string += spell.name;
  //                   }
  //                 }
  //                 str += level_string + "\n\r  ";
  //               } else if (cast_type === "X/Day" && x_day_entry) {
  //                 // Show them each in their own row
  //                 for (const spell_of_level of spells_of_level.filter(o => o.cast_type === cast_type)) {
  //                   const spell_id = spell_of_level.spell_id;
  //                   const x_day = x_day_entry.data[spell_id];
  //                   const spell = (api.smart_hash.spell as Spell[]).find(o => o._id === spell_id);
  //                   if (x_day && spell) {
  //                     str += `${spell.name} (${x_day}/Day)\n\r  `;
  //                   }
  //                 }
  //               }
  //             }
  //           }
  //         }
  //       }
  //     }
  //   }
  //   fbs = ci ? this.legendary_action_fbs.filter(o => o.level <= ci.challenge_rating) : this.legendary_action_fbs;
  //   if (fbs.length > 0) {
  //     str += "## Legendary Actions\n\r  ";
  //     let legendary_actions = ""; 
  //     if (this.legendaries_table.entries.length === 1) {
  //       legendary_actions = `${this.legendaries_table.entries[0].actions}`;
  //     } else if (ci) {
  //       legendary_actions = `${this.legendary_actions_for_cr(ci.challenge_rating)}`;
  //     } else {
  //       legendary_actions = "(found in the Resources table)";
  //     }
  //     str += `${this.small_name === "" ? this.name : "The " + this.small_name} can take ${legendary_actions} legendary actions, choosing from the options below. Only one legendary action option can be used at a time and only at the end of another creature's turn. ${this.small_name === "" ? this.name : "The " + this.small_name} regains spent legendary actions at the start of its turn.`;
  //     if (ci) {
  //       for (const fb of fbs) {
  //         str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: this.spellcasting_ability !== "None" ? this.spellcasting_ability : undefined })}\n\r  `;
  //       }
  //     } else {
  //       const crs = (common_util.unique_array(fbs.map(o => o.level)) as number[]).sort();
  //       if (crs.length === 1 && crs[0] <= this.min_challenge_rating) {
  //         for (const fb of fbs) {
  //           str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: this.spellcasting_ability !== "None" ? this.spellcasting_ability : undefined })}\n\r  `;
  //         }
  //       } else if (crs.length > 0) {
  //         for (const cr of crs) {
  //           str += `### At CR ${cr}\n\r  `;
  //           for (const fb of fbs.filter(o => o.level === cr)) {
  //             str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacements2({ clean_me: fb.description, obj: ci, spellcasting_ability: this.spellcasting_ability !== "None" ? this.spellcasting_ability : undefined })}\n\r  `;
  //           }
  //         }
  //       }
  //     }
  //   }
  //   return str;
  // }

  toDBObj(): any {
    const upgradable_hash: any = {};
    Object.keys(this.upgradable_hash).forEach(key => {
      upgradable_hash[key] = this.upgradable_hash[key].toDBObj();
    });
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      imported_object: this.imported_object,
      title: this.title,
      small_name: this.small_name,
      flavor_text: this.flavor_text,
      creature_type: this.creature_type,
      subtypes: this.subtypes,
      class_ids: this.class_ids,
      age_category: this.age_category,
      ability_scores: this.ability_scores.toDBObj(),
      hit_points_called: this.hit_points_called,
      hit_dice: this.hit_dice.map(o => o.toDBObj()),
      hit_dice_bonus: this.hit_dice_bonus.toDBObj(),
      extra_hp_pools: this.extra_hp_pools.map(o => {
        return { true_id: o.true_id, called: o.called, max_hp: o.max_hp.toDBObj() };
      }),
      finishing_blow: this.finishing_blow.toDBObj(),
      starting_hp: this.starting_hp ? this.starting_hp.toDBObj() : null,
      resources_table: this.resources_table.toDBObj(),
      spellcasting_table: this.spellcasting_table.toDBObj(),
      legendaries_table: this.legendaries_table.toDBObj(),
      attributes_table: this.attributes_table.toDBObj(),
      fbs: this.fbs.map(o => o.toDBObj()),
      challenge_rating: this.challenge_rating,
      min_challenge_rating: this.min_challenge_rating,
      max_challenge_rating: this.max_challenge_rating,
      name_table: this.name_table,
      initiative_modifier: this.initiative_modifier.toDBObj(),
      armor_class: this.armor_class.toDBObj(),
      ac_flavor: this.ac_flavor,
      size_ugn: this.size_ugn.toDBObj(),
      alignment: this.alignment,
      // this is a bug in the code!!! - Clara
      environments: this.environments,
      // settings: this.settings,
      native_planes: this.native_planes,
      group_id: this.group_id,
      gear_item_ids: this.gear_item_ids,
      spellcasting_ability: this.spellcasting_ability,
      dc: this.dc.toDBObj(),
      magic_attack: this.magic_attack.toDBObj(),
      non_magic_attack: this.non_magic_attack.toDBObj(),
      magic_dmg_bonus: this.magic_dmg_bonus.toDBObj(),
      non_magic_dmg_bonus: this.non_magic_dmg_bonus.toDBObj(),
      string_hash: {...this.string_hash},
      upgradable_hash,
      // ridable_type: this.ridable_type,
      // max_passengers: this.max_passengers,
      // attunement_slots: this.attunement_slots,
      // cost: this.cost,
      // mishap_description: this.mishap_description,
      // mishaps: this.mishaps.map(o => o.toDBObj()),
      // stations: this.stations.map(o => o.toDBObj()),
      simple_character: this.simple_character,
      swimming: this.swimming,
      flying: this.flying,
      table: this.table.toDBObj()
    };
  }

  toLight(): any {
    const upgradable_hash: any = {};
    Object.keys(this.upgradable_hash).forEach(key => {
      upgradable_hash[key] = this.upgradable_hash[key].toDBObj();
    });
    const base_db_obj = super.toLight();
    return {
      ...base_db_obj,
      imported_object: this.imported_object,
      title: this.title,
      small_name: this.small_name,
      flavor_text: this.flavor_text,
      creature_type: this.creature_type,
      subtypes: this.subtypes,
      class_ids: this.class_ids,
      age_category: this.age_category,
      ability_scores: this.ability_scores.toDBObj(),
      hit_points_called: this.hit_points_called,
      hit_dice: this.hit_dice.map(o => o.toDBObj()),
      hit_dice_bonus: this.hit_dice_bonus.toDBObj(),
      extra_hp_pools: this.extra_hp_pools.map(o => {
        return { true_id: o.true_id, called: o.called, max_hp: o.max_hp.toDBObj() };
      }),
      finishing_blow: this.finishing_blow.toDBObj(),
      starting_hp: this.starting_hp ? this.starting_hp.toDBObj() : null,
      resources_table: this.resources_table.toDBObj(),
      spellcasting_table: this.spellcasting_table.toDBObj(),
      legendaries_table: this.legendaries_table.toDBObj(),
      attributes_table: this.attributes_table.toDBObj(),
      // fbs: this.fbs.map(o => o.toDBObj()),
      _the_attack_types: this._the_attack_types,
      _the_effect_types: this._the_effect_types,
      _the_utility_details: this._the_utility_details,
      challenge_rating: this.challenge_rating,
      min_challenge_rating: this.min_challenge_rating,
      max_challenge_rating: this.max_challenge_rating,
      name_table: this.name_table,
      initiative_modifier: this.initiative_modifier.toDBObj(),
      armor_class: this.armor_class.toDBObj(),
      ac_flavor: this.ac_flavor,
      size_ugn: this.size_ugn.toDBObj(),
      alignment: this.alignment,
      // this is a bug in the code!!! - Clara
      environments: this.environments,
      // settings: this.settings,
      native_planes: this.native_planes,
      group_id: this.group_id,
      gear_item_ids: this.gear_item_ids,
      spellcasting_ability: this.spellcasting_ability,
      dc: this.dc.toDBObj(),
      magic_attack: this.magic_attack.toDBObj(),
      non_magic_attack: this.non_magic_attack.toDBObj(),
      magic_dmg_bonus: this.magic_dmg_bonus.toDBObj(),
      non_magic_dmg_bonus: this.non_magic_dmg_bonus.toDBObj(),
      string_hash: {...this.string_hash},
      upgradable_hash,
      // ridable_type: this.ridable_type,
      // max_passengers: this.max_passengers,
      // attunement_slots: this.attunement_slots,
      // cost: this.cost,
      // mishap_description: this.mishap_description,
      // mishaps: this.mishaps.map(o => o.toDBObj()),
      // stations: this.stations.map(o => o.toDBObj()),
      simple_character: this.simple_character,
      swimming: this.swimming,
      flying: this.flying,
      table: this.table.toDBObj()
    };
  }

  get creature_type_string() {
    return `${this.subtypes.length > 0 ? this.subtypes.join(" ") + " " : ""}${this.creature_type}`;
  }

  _the_effect_types: string[] = [];
  get the_effect_types(): string[] {
    if (this.loaded) {
      // if (this.name.includes("Adult") && this.name.includes("Black")) {
      //   console.log(this.actions.flatMap(o => o.the_effect_types));
      //   debugger;
      // }
      return [
        ...this.fbs.flatMap(o => o.the_effect_types),
      ];
    } else {
      return this._the_effect_types;
    }
  }

  _the_utility_details: string[] = [];
  get the_utility_details(): string[] {
    if (this.loaded) {
      return [
        ...this.fbs.flatMap(o => o.the_utility_details),
      ];
    } else {
      return this._the_utility_details;
    }
  }

  _the_attack_types: AttackType[] = [];
  get the_attack_types(): AttackType[] {
    if (this.loaded) {
      return [
        ...this.fbs.flatMap(o => o.the_attack_types),
      ];
    } else {
      return this._the_attack_types;
    }
  }

  get spell_dc(): UpgradableNumber {
    if (!this.dc.isBlank()) {
      return this.dc;
    } else {
      const dc = new UpgradableNumber(this.proficiency_modifier);
      if (this.spellcasting_ability !== "None") {
        const mod = this.ability_scores.getAbilityScore(this.spellcasting_ability);
        if (mod) {
          dc.merge(mod);
          dc.base += 8;
        } else {
          dc.base += 10;
        }
      } else {
        dc.base += 10;
      }
      return dc;
    }
  }

  get spell_attack(): UpgradableNumber {
    if (!this.magic_attack.isBlank()) {
      return this.magic_attack;
    } else {
      const spell_attack = new UpgradableNumber(this.proficiency_modifier);
      if (this.spellcasting_ability !== "None") {
        const mod = this.ability_scores.getAbilityScore(this.spellcasting_ability);
        if (mod) {
          spell_attack.merge(mod);
        }
      } 
      return spell_attack;
    }
  }

  skills_string(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.skill_profs_unchanged) {
      return this.attributes_table.entries[0].skills_string;
    }
    return "";
  }

  tools_string(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.tool_profs_unchanged) {
      return this.attributes_table.entries[0].tools_string;
    }
    return "";
  }

  get speed_string(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.speeds_unchanged) {
      return this.attributes_table.entries[0].speed_string;
    }
    return "";
  }

  get senses_string(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.senses_unchanged) {
      return this.attributes_table.entries[0].senses_string;
    }
    return "";
  }

  get flying(): boolean {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.speeds_unchanged) {
      const flying = this.attributes_table.entries[0].speeds["fly"];
      if (flying && flying > 0) {
        return true;
      }
    }
    return false;
  }

  set flying(value: boolean) {}

  get flying_string(): string {
    if (this.flying) {
      return " - Flying";
    }
    return "";
  }

  get swimming(): boolean {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.speeds_unchanged) {
      const swim = this.attributes_table.entries[0].speeds["swim"];
      if (swim && swim > 0) {
        return true;
      }
    }
    return false;
  }
  
  set swimming(value: boolean) {}

  get immunities_string(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.damage_immunities_unchanged) {
      return this.attributes_table.entries[0].immunities_string;
    }
    return "";
  }

  get resistances_string(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.damage_resistances_unchanged) {
      return this.attributes_table.entries[0].resistances_string;
    }
    return "";
  }

  get vulnerabilities_string(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.damage_vulnerabilities_unchanged) {
      return this.attributes_table.entries[0].vulnerabilities_string;
    }
    return "";
  }

  get condition_immunities_string(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.condition_immunities_unchanged) {
      return this.attributes_table.entries[0].condition_immunities_string;
    }
    return "";
  }

  get damage_threshold_string() {
    if (this.damage_threshold === 0) {
      return "";
    } else {
      return `${this.damage_threshold}`;
    }
  }

  get saving_throws(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.save_profs_unchanged) {
      return this.attributes_table.entries[0].saving_throws_string;
    }
    return "";
  }

  get languages() {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.condition_immunities_unchanged) {
      return this.attributes_table.entries[0].languages;
    }
    return "";
  }

  get legendary() {
    // if (this.name.includes("Werewolf")) {
    //   console.log(this.legendaries_table);
    // }
    return this.legendaries_table.entries.filter(o => 
      (o.actions > 0) || 
      (o.resistances > 0)).length > 0;
  }

  get use_name() {
    return this.get_use_name();
  }

  get_use_name() {
    return this.title === "" ? super.get_use_name() : `${super.get_use_name()}, ${this.title}`;
  }

  copy(copyMe: Creature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.creature_type = copyMe.creature_type;
    this.small_name = copyMe.small_name;
    this.flavor_text = copyMe.flavor_text;
    this.connected = copyMe.connected;
    this.title = copyMe.title;
    this.ability_scores = copyMe.ability_scores.clone();
    this.subtypes = [...copyMe.subtypes];
    this.class_ids = [...copyMe.class_ids];
    this.age_category = copyMe.age_category;

    this.hit_points_called = copyMe.hit_points_called;
    this.hit_dice = copyMe.hit_dice.map(o => o.clone());
    this.hit_dice_bonus = copyMe.hit_dice_bonus.clone();
    this.extra_hp_pools = copyMe.extra_hp_pools.map(o => { 
      return {
        true_id: o.true_id,
        called: o.called, 
        max_hp: o.max_hp.clone()
      };
    });
    this.finishing_blow = copyMe.finishing_blow.clone();
    this.starting_hp = copyMe.starting_hp ? copyMe.starting_hp.clone() : null;

    this.challenge_rating = copyMe.challenge_rating;
    this.min_challenge_rating = copyMe.min_challenge_rating;
    this.max_challenge_rating = copyMe.max_challenge_rating;
    this.name_table = copyMe.name_table;
    this.attributes_table = copyMe.attributes_table.clone();
    
    this.initiative_modifier = copyMe.initiative_modifier.clone();
    this.armor_class = copyMe.armor_class.clone();
    this.ac_flavor = copyMe.ac_flavor;
    
    this.resources_table = copyMe.resources_table.clone();
    this.spellcasting_table = copyMe.spellcasting_table.clone();
    this.legendaries_table = copyMe.legendaries_table.clone();
    this.fbs = copyMe.fbs.map(o => o.clone());
    
    this.size_ugn = copyMe.size_ugn.clone();
    this.alignment = copyMe.alignment;
    
    this.environments = [...copyMe.environments];
    // this.settings = [...copyMe.settings];
    this.native_planes = [...copyMe.native_planes];

    this.group_id = copyMe.group_id;
    this.gear_item_ids = [...copyMe.gear_item_ids];

    this.spellcasting_ability = copyMe.spellcasting_ability;
   
    this.dc = copyMe.dc.clone();
    this.magic_attack = copyMe.magic_attack.clone();
    this.non_magic_attack = copyMe.non_magic_attack.clone();
    this.magic_dmg_bonus = copyMe.magic_dmg_bonus.clone();
    this.non_magic_dmg_bonus = copyMe.non_magic_dmg_bonus.clone();
    this.string_hash = {...copyMe.string_hash};
    const upgradable_hash: IStringUpgradableNumberHash = {};
    Object.keys(copyMe.upgradable_hash).forEach(key => {
      upgradable_hash[key] = copyMe.upgradable_hash[key].clone();
    });
    this.upgradable_hash = upgradable_hash;
    // this.ridable_type = copyMe.ridable_type;
    // this.max_passengers = copyMe.max_passengers;
    // this.attunement_slots = copyMe.attunement_slots;
    // this.cost = copyMe.cost
    // this.mishap_description = copyMe.mishap_description;
    // this.mishaps = copyMe.mishaps.map(o => o.clone());
    // this.stations = copyMe.stations.map(o => o.clone());
    this.simple_character = copyMe.simple_character;
    this.table = copyMe.table.clone();
  }

  clone(drop_ids = false): Creature {
    const new_obj = new Creature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copyTemplate(copyMe: TemplateBase): void {
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.source_books_old = [];
    copyMe.source_books_old.forEach(sb => {
      this.source_books_old.push({...sb});
    });
    const obj = {...copyMe.item};
    this.table = obj && obj.table ? new AttachedTable(obj.table) : new AttachedTable();
    this.imported_object = obj && obj.imported_object ? obj.imported_object : null;
    this.title = obj && obj.title ? obj.title : "";
    this.small_name = obj && obj.small_name ? obj.small_name : "";
    this.flavor_text = obj && obj.flavor_text ? obj.flavor_text : "";
    (this.creature_type as string) = obj ? (obj.creature_type ? common_util.capitalize_firsts(obj.creature_type) : common_util.capitalize_firsts(obj.type)) : "";
    if (!CREATURE_TYPES.includes(this.creature_type)) {
      this.creature_type = "None";
    }
    this.subtypes = obj && obj.subtypes ? obj.subtypes : [];
    this.class_ids = obj && obj.class_ids ? obj.class_ids : [];
    this.age_category = obj && obj.age_category ? obj.age_category : "";
    this.challenge_rating = obj ? obj.challenge_rating : 0;
    this.min_challenge_rating = obj && obj.min_challenge_rating ? obj.min_challenge_rating : this.challenge_rating;
    this.max_challenge_rating = obj && obj.max_challenge_rating ? obj.max_challenge_rating : this.challenge_rating;
    this.name_table = obj && obj.name_table ? obj.name_table : [];
    this.ability_scores = obj ? new AbilityScoresUpgradable(obj.ability_scores) : new AbilityScoresUpgradable(8);
    // this.old_resources = obj && obj.resources ? obj.resources.map((o: any) => {
    //   return {...o};
    // }) : [];
    this.resources_table = obj && obj.resources_table ? new AttachedTable(obj.resources_table) : new AttachedTable();
    this.spellcasting_table = obj && obj.spellcasting_table ? new SpellcastingTable(obj.spellcasting_table) : new SpellcastingTable();
    
    if (obj && obj.fbs) {
      this.fbs = obj.fbs.map((o: any) => { return new FeatureBase(o); });
    } 
    this.legendaries_table = obj && obj.legendaries_table ? new LegendariesTable(obj.legendaries_table) : new LegendariesTable();

    this.hit_points_called = obj && obj.hit_points_called ? obj.hit_points_called : "";
    this.hit_dice = []; 
    if (obj && obj.hit_dice) {
      if (obj.hit_dice.length) {
        obj.hit_dice.forEach((hd: any) => {
          this.hit_dice.push(new HitDiceUpgradable(hd));
        });
      } else {
        this.hit_dice.push(new HitDiceUpgradable(obj.hit_dice));
      }
    }
    this.hit_dice_bonus = obj && obj.hit_dice_bonus ? new UpgradableNumber(obj.hit_dice_bonus) : new UpgradableNumber();
    this.extra_hp_pools = [];
    if (obj && obj.extra_hp_pools) {
      for (const o of obj.extra_hp_pools) {
        this.extra_hp_pools.push({ true_id: o.id, called: o.called, max_hp: new UpgradableNumber(o.max_hp) });
      }
    }
    this.finishing_blow = obj && obj.finishing_blow ? new UpgradableNumber(obj.finishing_blow) : new UpgradableNumber();
    this.starting_hp = obj && obj.starting_hp ? new UpgradableNumber(obj.starting_hp) : null;

    this.attributes_table = obj && obj.attributes_table ? new AttributesTable(obj.attributes_table) : new AttributesTable();
    
    this.initiative_modifier = obj ? new UpgradableNumber(obj.initiative_modifier) : new UpgradableNumber();
    this.armor_class = obj ? new UpgradableNumber(obj.armor_class) : new UpgradableNumber();
    this.ac_flavor = obj && obj.ac_flavor ? obj.ac_flavor : "";
    
    this.size_ugn = obj.size_ugn ? new UpgradableNumber(obj.size_ugn) : new UpgradableNumber(3);
    this.alignment = obj ? common_util.capitalize_firsts(obj.alignment) : "Neutral";
    
    this.environments = obj && obj.environments ? obj.environments : [];
    // this.settings = obj && obj.settings ? obj.settings : [];
    this.native_planes = obj && obj.native_planes ? obj.native_planes : [];

    this.group_id = obj && obj.group_id ? obj.group_id : "";
    this.gear_item_ids = obj && obj.gear_item_ids ? obj.gear_item_ids : [];

    this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "";
   
    this.dc = obj.dc ? new UpgradableNumber(obj.dc) : new UpgradableNumber();
    if (this.dc.isBasic() && this.dc.base === -1) {
      this.dc.base = 0;
    }
    this.magic_attack = obj.magic_attack ? new UpgradableNumber(obj.magic_attack) : new UpgradableNumber();
    if (this.magic_attack.isBasic() && this.magic_attack.base === -1) {
      this.magic_attack.base = 0;
    }
    this.non_magic_attack = obj.non_magic_attack ? new UpgradableNumber(obj.non_magic_attack) : new UpgradableNumber();
    if (this.non_magic_attack.isBasic() && this.non_magic_attack.base === -1) {
      this.non_magic_attack.base = 0;
    }
    this.magic_dmg_bonus = obj.magic_dmg_bonus ? new UpgradableNumber(obj.magic_dmg_bonus) : new UpgradableNumber();
    if (this.magic_dmg_bonus.isBasic() && this.magic_dmg_bonus.base === -1) {
      this.magic_dmg_bonus.base = 0;
    }
    this.non_magic_dmg_bonus = obj.non_magic_dmg_bonus ? new UpgradableNumber(obj.non_magic_dmg_bonus) : new UpgradableNumber();
    if (this.non_magic_dmg_bonus.isBasic() && this.non_magic_dmg_bonus.base === -1) {
      this.non_magic_dmg_bonus.base = 0;
    }

    this.string_hash = obj.string_hash ? obj.string_hash : {};
    this.upgradable_hash = {};
    if (obj.upgradable_hash) {
      Object.keys(obj.upgradable_hash).forEach(key => {
        this.upgradable_hash[key] = new UpgradableNumber(obj.upgradable_hash[key]);
      });
    }
    // this.ridable_type = obj.ridable_type ? obj.ridable_type : "None";
    // this.max_passengers = obj.max_passengers ? obj.max_passengers : 0;
    // this.attunement_slots = obj.attunement_slots ? obj.attunement_slots : 0;
    // this.cost = obj.cost ? obj.cost : 0;
    // this.mishap_description = obj.mishap_description ? obj.mishap_description : "";
    // this.mishaps = [];
    // if (obj && obj.mishaps && obj.mishaps.length > 0) {
    //   obj.mishaps.forEach((o: any) => {
    //     const feature = new Feature(o);
    //     feature.parent_type = "creature";
    //     feature.parent_id = this._id;
    //     feature.base_id = this._id;
    //     this.mishaps.push(feature);
    //   });
    // }
    // this.stations = [];
    // if (obj && obj.stations && obj.stations.length > 0) {
    //   obj.stations.forEach((o: any) => {
    //     const feature = new Feature(o);
    //     feature.parent_type = "creature";
    //     feature.parent_id = this._id;
    //     feature.base_id = this._id;
    //     this.stations.push(feature);
    //   });
    // }
    this.simple_character = obj && obj.simple_character ? obj.simple_character : false;

    this.connected = false;
  }

  get_proficiency_modifier(cr: number) {
    const cr_div_5 = cr < 1 ? 0 : Math.floor((cr - 1) / 4);
    return cr_div_5 + 2;
  }

  get proficiency_modifier() {
    return this.get_proficiency_modifier(this.challenge_rating);
  }

  getBasicScore(stat: string, cr = -1) {
    if (cr === -1) {
      cr = this.challenge_rating;
    }
    const ugn_stat = this.ability_scores.getAbilityScore(stat);
    if (ugn_stat) {
      if (ugn_stat.table_field.length > 0) {
        const entry = this.table.getEntry(cr);
        if (entry && entry.data[stat] !== undefined) {
          return entry.data[stat];
        }
      } else {
        return ugn_stat.base;
      }
    }
    return 0;
  }

  getBasicModifier(stat: string, cr = -1) {
    return Math.floor((this.getBasicScore(stat, cr) - 10) / 2);
  }

  copy5e(copyMe: any, senses: Sense[]): void {
    // this.imported_object = copyMe;
    // this.name = copyMe.name;
    // this.challenge_rating = copyMe.challenge_rating;
    // this.alignment = common_util.capitalize_firsts(copyMe.alignment);
    // this.languages = copyMe.languages;
    // this.xp = copyMe.xp;
    // this.condition_immunities = copyMe.condition_immunities;
    // this.size = copyMe.size;
    // this.armor_class = copyMe.armor_class;
    // this.ac_flavor = copyMe.ac_flavor;
    // this.hp.max = copyMe.hit_points;
    // this.creature_type = common_util.capitalize_firsts(copyMe.type);
    // if (copyMe.subtype) {
    //   this.subtype = common_util.capitalize_firsts(copyMe.subtype);
    // }
    // // const hd_pieces = copyMe.hit_dice.split("d");
    // // const hit_dice = new HitDice();
    // // hit_dice.size = hd_pieces[1];
    // // hit_dice.count = hd_pieces[0];
    // // this.hit_dice = hit_dice;
    // Object.keys(copyMe.speed).forEach((key: string) => {
    //   const speed = copyMe.speed[key];
    //   if (typeof speed === "string") {
    //     const pieces = speed.split(" ft.");
    //     switch(key) {
    //       case "walk": 
    //         this.speed.walk = +pieces[0];
    //       break;
    //       case "burrow": 
    //         this.speed.burrow = +pieces[0];
    //       break;
    //       case "climb": 
    //         this.speed.climb = +pieces[0];
    //       break;
    //       case "fly": 
    //         this.speed.fly = +pieces[0];
    //       break;
    //       case "swim": 
    //         this.speed.swim = +pieces[0];
    //       break;
    //     }
    //   } else if (key === "hover" && speed) {
    //     this.speed.hover = 1;
    //   }
    // });
    // Object.keys(copyMe.senses).forEach((key: string) => {
    //   const sense = copyMe.senses[key];

    //   if (["darkvision","blind_sight","blind_sense","true_sight","tremor_sense"].includes(key)) {
    //     let my_sense_name = "";
    //     switch(key) {
    //       case "darkvision":
    //         my_sense_name = "Darkvision";
    //       break;
    //       case "blind_sight":
    //       case "blind_sense":
    //         my_sense_name = "Blind Sight";
    //       break;
    //       case "true_sight":
    //         my_sense_name = "True Sight";
    //       break;
    //       case "tremor_sense":
    //         my_sense_name = "Tremor Sense";
    //       break;
    //     }
    //     const sense_finder = senses.filter(o => o.name === my_sense_name);
    //     if (sense_finder.length === 1) {
    //       const the_sense = sense_finder[0];
    //       const char_sense = new CharacterSense();
    //       char_sense.name = the_sense.name;
    //       char_sense.sense_id = the_sense._id;
    //       const pieces = sense.split(" ft.");
    //       char_sense.range = pieces[0];
    //       this.senses.push(char_sense);
    //     }
    //   } else {
    //     this.passives[key] = copyMe.senses[key];
    //   }
    // });
    // copyMe.condition_immunities.forEach((ci: any) => {
    //   this.condition_immunities.push(ci.name);
    // });
    // copyMe.damage_immunities.forEach((dm: any) => {
    //   const damage_multiplier = new DamageMultiplier();
    //   damage_multiplier.damage_types.push(dm);
    //   damage_multiplier.multiplier = 0;
    //   this.damage_multipliers.push(damage_multiplier);
    // });
    // copyMe.damage_resistances.forEach((dm: any) => {
    //   const damage_multiplier = new DamageMultiplier();
    //   damage_multiplier.damage_types.push(dm);
    //   damage_multiplier.multiplier = 0.5;
    //   this.damage_multipliers.push(damage_multiplier);
    // });
    // copyMe.damage_vulnerabilities.forEach((dm: any) => {
    //   const damage_multiplier = new DamageMultiplier();
    //   damage_multiplier.damage_types.push(dm);
    //   damage_multiplier.multiplier = 2;
    //   this.damage_multipliers.push(damage_multiplier);
    // });
    // copyMe.proficiencies.forEach((prof: any) => {
    //   if (prof.proficiency.name.includes("Saving Throw")) {
    //     const pieces = prof.proficiency.name.split("Saving Throw: ");
    //     this.saving_throws[pieces[1]] = prof.value;
    //   } else if (prof.proficiency.name.includes("Skill")) {
    //     const pieces = prof.proficiency.name.split("Skill: ");
    //     this.skill_proficiencies[pieces[1]] = prof.value;
    //   } else if (prof.proficiency.name.includes("Tool")) {
    //     const pieces = prof.proficiency.name.split("Tool: ");
    //     this.tool_proficiencies[pieces[1]] = prof.value;
    //   }
    // });
    // this.ability_scores.strength = copyMe.strength;
    // this.ability_scores.dexterity = copyMe.dexterity;
    // this.ability_scores.constitution = copyMe.constitution;
    // this.ability_scores.intelligence = copyMe.intelligence;
    // this.ability_scores.wisdom = copyMe.wisdom;
    // this.ability_scores.charisma = copyMe.charisma;
    // if (copyMe.actions) {
    //   copyMe.actions.forEach((action: any) => {
    //     const feature = common_util.new_feature_of_type("Creature Ability");
    //     feature.name = action.name;
    //     feature.description = action.desc;
    //     if (action.name === "Multiattack") {

    //     } else {
    //       const ability = new CreatureAbility();
    //       ability.name = action.name;
    //       ability.description = action.desc;
    //       if (action.attack_bonus) {
    //         ability.attack_bonus = action.attack_bonus;
    //       }
    //       if (action.dc) {
    //         ability.saving_throw_ability_score = action.dc.dc_type.name;
    //         ability.dc = action.dc.dc_value;
    //       } 
    //       if (action.damage && action.damage.length > 0) {
    //         const effect = new AbilityEffect();
    //         const potence = new Potence();
    //         let flat = 0;
    //         let size = 0;
    //         let count = 0;
    //         if (action.damage[0].from) {
    //           if (action.damage[0].from.length > 0) {
    //             effect.type = action.damage[0].from[0].damage_type.name;
    //             effect.attack_type = ability.attack_bonus > 0 ? "Melee" : (ability.dc ? "Save" : "?");
    //             potence.rolls.type = effect.type;
    //             if (action.damage[0].from[0].damage_dice) {
    //               const dice = action.damage[0].from[0].damage_dice;
    //               if (dice.includes("d")) {
    //                 const pieces1 = dice.split("d");
    //                 count = +pieces1[0];
    //                 if (pieces1[1].includes("+")) {
    //                   const pieces2 = pieces1[1].split("+");
    //                   size = +pieces2[0];
    //                   flat = +pieces2[1];
    //                 } else {
    //                   size = +pieces1[1];
    //                 }
    //               }
    //             }
    //           }
    //         } else {
    //           effect.type = action.damage[0].damage_type.name;
    //           effect.attack_type = ability.attack_bonus > 0 ? "Melee" : (ability.dc ? "Save" : "?");
    //           potence.rolls.type = effect.type;
    //           if (action.damage[0].damage_dice) {
    //             const dice = action.damage[0].damage_dice;
    //             if (dice.includes("d")) {
    //               const pieces1 = dice.split("d");
    //               count = +pieces1[0];
    //               if (pieces1[1].includes("+")) {
    //                 const pieces2 = pieces1[1].split("+");
    //                 size = +pieces2[0];
    //                 flat = +pieces2[1];
    //               } else {
    //                 size = +pieces1[1];
    //               }
    //             }
    //           }
    //         }
    //         potence.rolls.flat = +flat;
    //         potence.rolls.size = +size;
    //         potence.rolls.count = +count;
    //         effect.potences.push(potence);
    //         ability.effects.push(effect);
    //       }
    //       feature.feature_type = "Creature Ability";
    //       feature.the_feature = ability;
    //     }
    //     this.actions.push(feature);
    //   });
    // }
    // if (copyMe.legendary_actions) {
    //   copyMe.legendary_actions.forEach((action: any) => {
    //     const feature = common_util.new_feature_of_type("Creature Ability");
    //     feature.name = action.name;
    //     feature.description = action.desc;
    //     if (action.name === "Multiattack") {

    //     } else {
    //       const ability = new Ability();
    //       ability.name = action.name;
    //       ability.description = action.desc;
    //       if (action.attack_bonus) {
    //         ability.attack_bonus = action.attack_bonus;
    //       }
    //       if (action.dc) {
    //         ability.saving_throw_ability_score = action.dc.dc_type.name;
    //         ability.dc = action.dc.dc_value;
    //       }
    //       if (action.damage && action.damage.length > 0) {
    //         const effect = new AbilityEffect();
    //         effect.type = action.damage[0].damage_type.name;
    //         effect.attack_type = ability.attack_bonus.isBlank() ? (ability.dc.isBlank() ? "Save" : "?") : "Melee";
    //         const potence = new Potence();
    //         potence.rolls.type = effect.type;
    //         let flat = 0;
    //         let size = 0;
    //         let count = 0;
    //         if (action.damage[0].damage_dice) {
    //           const dice = action.damage[0].damage_dice;
    //           if (dice.includes("d")) {
    //             const pieces1 = dice.split("d");
    //             count = +pieces1[0];
    //             if (pieces1[1].includes("+")) {
    //               const pieces2 = pieces1[1].split("+");
    //               size = +pieces2[0];
    //               flat = +pieces2[1];
    //             } else {
    //               size = +pieces1[1];
    //             }
    //           }
    //         }
    //         potence.rolls.flat = +flat;
    //         potence.rolls.size = +size;
    //         potence.rolls.count = +count;
    //         effect.potences.push(potence);
    //         ability.effects.push(effect);
    //       }
    //       feature.feature_type = "Creature Ability";
    //       feature.the_feature = ability;
    //     }
    //     this.legendary_actions.push(feature);
    //   });
    // }
    // if (copyMe.special_abilities) {
    //   copyMe.special_abilities.forEach((action: any) => {
    //     const feature = new Feature();
    //     feature.name = action.name;
    //     feature.description = action.desc;
    //     if (action.name === "Multiattack") {

    //     } else {
    //       const ability = new Ability();
    //       ability.name = action.name;
    //       ability.description = action.desc;
    //       if (action.attack_bonus) {
    //         ability.attack_bonus = action.attack_bonus;
    //       }
    //       if (action.dc) {
    //         ability.saving_throw_ability_score = action.dc.dc_type.name;
    //         ability.dc = action.dc.dc_value;
    //       }
    //       if (action.damage && action.damage.length > 0) {
    //         const effect = new AbilityEffect();
    //         effect.type = action.damage[0].damage_type.name;
    //         effect.attack_type = ability.attack_bonus.isBlank() ? (ability.dc.isBlank() ? "Save" : "?") : "Melee";
    //         const potence = new Potence();
    //         potence.rolls.type = effect.type;
    //         let flat = 0;
    //         let size = 0;
    //         let count = 0;
    //         if (action.damage[0].damage_dice) {
    //           const dice = action.damage[0].damage_dice;
    //           if (dice.includes("d")) {
    //             const pieces1 = dice.split("d");
    //             count = +pieces1[0];
    //             if (pieces1[1].includes("+")) {
    //               const pieces2 = pieces1[1].split("+");
    //               size = +pieces2[0];
    //               flat = +pieces2[1];
    //             } else {
    //               size = +pieces1[1];
    //             }
    //           }
    //         }
    //         potence.rolls.flat = +flat;
    //         potence.rolls.size = +size;
    //         potence.rolls.count = +count;
    //         effect.potences.push(potence);
    //         ability.effects.push(effect);
    //       }
    //       feature.feature_type = "Creature Ability";
    //       feature.the_feature = ability;
    //     }
    //     this.special_abilities.push(feature);
    //   });
    // }
  }

  recopy5e(senses: Sense[]): void {
    this.copy5e(this.imported_object, senses);
  }
}

export interface CreatureDetails {
  creature_type: string;
  subtypes: string[];
  age_category: string;
  size_num: number;
  min_challenge_rating: number;
  max_challenge_rating: number;
  flying: boolean;
  swimming: boolean;
  _id: string;
}

export function copyCreatureDetails(source: CreatureDetails, destination: CreatureDetails) {
  destination._id = source._id;
  destination.creature_type = source.creature_type;
  destination.subtypes = [...source.subtypes];
  destination.max_challenge_rating = source.max_challenge_rating;
  destination.min_challenge_rating = source.min_challenge_rating;
  destination.size_num = source.size_num;
  destination.flying = source.flying;
  destination.swimming = source.swimming;
}

// These are like Gifts for Characters, but to go on CreatureInstances instead.
// They have their own level progression, seperate from the regular CR, 
// and their FBs based on the Gift Level get added to their matching group on the CI.
// Likewise, things from the tables get merged together with the appropriate things on the CI.
export class CreatureGift extends ModelBase {
  data_type: DataType = "creature_gift";

  max_level = 1;
  limit: CreatureLimitation = new CreatureLimitation();
  name_formula = "{gift.name} {gift.level} {base.name}"; // Used to change default name to have the gift name be part of it.
  // Shadow Dragon works like this "{replace.Dragon.Shadow Dragon}"
  override_type: CreatureType = "None";
  bonus_subtypes: string[] = []; // Extra Subtypes added.  Shadow Dragon adds Shadow.
  remove_subtypes: string[] = [];

  table: AttachedTable = new AttachedTable(); // Has modifiers for Ability Scores, AC, Size, Hit Dice Count, and CR.  
  // CR being increased will affect Proficiency Bonus, 
  // but otherwise all things that look at CR use the CR from before the modification.
  attributes_table: AttributesTable = new AttributesTable();
  resources_table: AttachedTable = new AttachedTable();
  spellcasting_table: SpellcastingTable = new SpellcastingTable();
  legendaries_table: LegendariesTable = new LegendariesTable();
  damage_type_overrides_table: DamageTypeOverridesTable = new DamageTypeOverridesTable();

  fbs: FeatureBase[] = [];
  get action_fbs() {
    return this.fbs.filter(o => o.creature_fb_type === "Actions");
  }
  get traits() {
    return this.fbs.filter(o => o.creature_fb_type === "Traits");
  }
  get legendary_action_fbs() {
    return this.fbs.filter(o => o.creature_fb_type === "Legendary Actions");
  }
  get lair_action_fbs() {
    return this.fbs.filter(o => o.creature_fb_type === "Lair Actions");
  }

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.max_level = obj.max_level ? obj.max_level : 1;
      this.limit = obj.limit ? new CreatureLimitation(obj.limit) : new CreatureLimitation();
      this.name_formula = obj.name_formula ? obj.name_formula : "{gift.name} {gift.level} {base.name}";
      this.override_type = obj.override_type ? obj.override_type : "None";
      this.bonus_subtypes = obj.bonus_subtypes ? obj.bonus_subtypes : [];
      this.remove_subtypes = obj.remove_subtypes ? obj.remove_subtypes : [];
      
      this.table = obj.table ? new AttachedTable(obj.table) : new AttachedTable();
      this.attributes_table = obj.attributes_table ? new AttributesTable(obj.attributes_table) : new AttributesTable();
      this.resources_table = obj.resources_table ? new AttachedTable(obj.resources_table) : new AttachedTable();
      this.spellcasting_table = obj.spellcasting_table ? new SpellcastingTable(obj.spellcasting_table) : new SpellcastingTable();
      this.legendaries_table = obj.legendaries_table ? new LegendariesTable(obj.legendaries_table) : new LegendariesTable();
      this.damage_type_overrides_table = obj.damage_type_overrides_table ? new DamageTypeOverridesTable(obj.damage_type_overrides_table) : new DamageTypeOverridesTable();
      
      if (obj.fbs) {
        this.fbs = obj.fbs.map((o: any) => new FeatureBase(o));
      } else {
        if (obj.action_fbs) {
          let fbs: FeatureBase[] = obj.action_fbs.map((o: any) => new FeatureBase(o));
          fbs = fbs.filter(o => o.features.length > 0);
          for (const fb of fbs) {
            fb.creature_fb_type = "Actions";
          }
          this.fbs.push(...fbs);
        } 
        
        if (obj.legendary_action_fbs) {
          let fbs: FeatureBase[] = obj.legendary_action_fbs.map((o: any) => new FeatureBase(o));
          fbs = fbs.filter(o => o.features.length > 0);
          for (const fb of fbs) {
            fb.creature_fb_type = "Legendary Actions";
          }
          this.fbs.push(...fbs);
        } 
        if (obj.lair_action_fbs) {
          let fbs: FeatureBase[] = obj.lair_action_fbs.map((o: any) => new FeatureBase(o));
          fbs = fbs.filter(o => o.features.length > 0);
          for (const fb of fbs) {
            fb.creature_fb_type = "Lair Actions";
          }
          this.fbs.push(...fbs);
        } 
        if (obj.traits) {
          let fbs: FeatureBase[] = obj.traits.map((o: any) => new FeatureBase(o));
          fbs = fbs.filter(o => o.features.length > 0);
          for (const fb of fbs) {
            fb.creature_fb_type = "Traits";
          }
          this.fbs.push(...fbs);
        } 
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.max_level = obj.max_level ? obj.max_level : 1;
  //     this.limit = obj.limit ? new CreatureLimitation(obj.limit) : new CreatureLimitation();
  //     this.name_formula = obj.name_formula ? obj.name_formula : "{gift.name} {gift.level} {base.name}";
  //     this.override_type = obj.override_type ? obj.override_type : "None";
  //     this.bonus_subtypes = obj.bonus_subtypes ? obj.bonus_subtypes : [];
  //     this.remove_subtypes = obj.remove_subtypes ? obj.remove_subtypes : [];
      
  //     this.table = obj.table ? new AttachedTable(obj.table) : new AttachedTable();
  //     this.attributes_table = obj.attributes_table ? new AttributesTable(obj.attributes_table) : new AttributesTable();
  //     this.resources_table = obj.resources_table ? new AttachedTable(obj.resources_table) : new AttachedTable();
  //     this.spellcasting_table = obj.spellcasting_table ? new SpellcastingTable(obj.spellcasting_table) : new SpellcastingTable();
  //     this.legendaries_table = obj.legendaries_table ? new LegendariesTable(obj.legendaries_table) : new LegendariesTable();
  //     this.damage_type_overrides_table = obj.damage_type_overrides_table ? new DamageTypeOverridesTable(obj.damage_type_overrides_table) : new DamageTypeOverridesTable();
      
  //     if (obj.fbs) {
  //       this.fbs = obj.fbs.map((o: any) => new FeatureBase(o));
  //     } else {
  //       if (obj.action_fbs) {
  //         let fbs: FeatureBase[] = obj.action_fbs.map((o: any) => new FeatureBase(o));
  //         fbs = fbs.filter(o => o.features.length > 0);
  //         for (const fb of fbs) {
  //           fb.creature_fb_type = "Actions";
  //         }
  //         this.fbs.push(...fbs);
  //       } 
        
  //       if (obj.legendary_action_fbs) {
  //         let fbs: FeatureBase[] = obj.legendary_action_fbs.map((o: any) => new FeatureBase(o));
  //         fbs = fbs.filter(o => o.features.length > 0);
  //         for (const fb of fbs) {
  //           fb.creature_fb_type = "Legendary Actions";
  //         }
  //         this.fbs.push(...fbs);
  //       } 
  //       if (obj.lair_action_fbs) {
  //         let fbs: FeatureBase[] = obj.lair_action_fbs.map((o: any) => new FeatureBase(o));
  //         fbs = fbs.filter(o => o.features.length > 0);
  //         for (const fb of fbs) {
  //           fb.creature_fb_type = "Lair Actions";
  //         }
  //         this.fbs.push(...fbs);
  //       } 
  //       if (obj.traits) {
  //         let fbs: FeatureBase[] = obj.traits.map((o: any) => new FeatureBase(o));
  //         fbs = fbs.filter(o => o.features.length > 0);
  //         for (const fb of fbs) {
  //           fb.creature_fb_type = "Traits";
  //         }
  //         this.fbs.push(...fbs);
  //       } 
  //     }
  //   }
  // }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      max_level: this.max_level,
      limit: this.limit.toDBObj(),
      name_formula: this.name_formula,
      override_type: this.override_type,
      bonus_subtypes: this.bonus_subtypes,
      remove_subtypes: this.remove_subtypes,
      table: this.table.toDBObj(),
      attributes_table: this.attributes_table.toDBObj(),
      resources_table: this.resources_table.toDBObj(),
      spellcasting_table: this.spellcasting_table.toDBObj(),
      legendaries_table: this.legendaries_table.toDBObj(),
      damage_type_overrides_table: this.damage_type_overrides_table.toDBObj(),
      fbs: this.fbs.map(o => o.toDBObj()),
    };
  }

  get the_effect_types(): string[] {
    // if (this.name.includes("Adult") && this.name.includes("Black")) {
    //   console.log(this.actions.flatMap(o => o.the_effect_types));
    //   debugger;
    // }
    return this.fbs.flatMap(o => o.the_effect_types);
  }

  get the_utility_details(): string[] {
    return this.fbs.flatMap(o => o.the_utility_details);
  }

  get the_attack_types(): AttackType[] {
    return this.fbs.flatMap(o => o.the_attack_types);
  }

  get immunities_string(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.damage_immunities_unchanged) {
      return this.attributes_table.entries[0].immunities_string;
    }
    return "";
  }

  get resistances_string(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.damage_resistances_unchanged) {
      return this.attributes_table.entries[0].resistances_string;
    }
    return "";
  }

  get vulnerabilities_string(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.damage_vulnerabilities_unchanged) {
      return this.attributes_table.entries[0].vulnerabilities_string;
    }
    return "";
  }

  get condition_immunities_string(): string {
    if (this.attributes_table.entries.length > 0 && this.attributes_table.condition_immunities_unchanged) {
      return this.attributes_table.entries[0].condition_immunities_string;
    }
    return "";
  }

  get legendary() {
    return this.legendaries_table.entries.filter(o => 
      (o.actions > 0) || 
      (o.resistances > 0)).length > 0;
  }

  get use_name() {
    return this.get_use_name();
  }

  get_use_name() {
    return this.name;
  }

  copy(copyMe: CreatureGift, drop_ids = false): void {
    super.copy(copyMe, drop_ids);

    this.max_level = copyMe.max_level;
    this.limit = copyMe.limit.clone();
    this.name_formula = copyMe.name_formula;
    this.override_type = copyMe.override_type;
    this.bonus_subtypes = [...copyMe.bonus_subtypes];
    this.remove_subtypes = [...copyMe.remove_subtypes];
    
    this.table = copyMe.table.clone();
    this.attributes_table = copyMe.attributes_table.clone();
    this.resources_table = copyMe.resources_table.clone();
    this.spellcasting_table = copyMe.spellcasting_table.clone();
    this.legendaries_table = copyMe.legendaries_table.clone();
    this.damage_type_overrides_table = copyMe.damage_type_overrides_table.clone();

    this.fbs = copyMe.fbs.map(o => o.clone());
  }

  clone(drop_ids = false): CreatureGift {
    const new_obj = new CreatureGift();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copyTemplate(copyMe: TemplateBase): void {
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.source_books_old = [];
    for (const sb of copyMe.source_books_old) {
      this.source_books_old.push({...sb});
    }
    const obj = {...copyMe.item};
    this.max_level = obj && obj.max_level ? obj.max_level : 1;
    this.limit = obj && obj.limit ? new CreatureLimitation(obj.limit) : new CreatureLimitation();
    this.name_formula = obj && obj.name_formula ? obj.name_formula : "{gift.name} {gift.level} {base.name}";
    this.override_type = obj && obj.override_type ? obj.override_type : "None";
    this.bonus_subtypes = obj && obj.bonus_subtypes ? [...obj.bonus_subtypes] : [];
    this.remove_subtypes = obj && obj.remove_subtypes ? [...obj.remove_subtypes] : [];
    this.table = obj && obj.table ? new AttachedTable(obj.table) : new AttachedTable();
    this.attributes_table = obj && obj.attributes_table ? new AttributesTable(obj.attributes_table) : new AttributesTable();
    this.resources_table = obj && obj.resources_table ? new AttachedTable(obj.resources_table) : new AttachedTable();
    this.spellcasting_table = obj && obj.spellcasting_table ? new SpellcastingTable(obj.spellcasting_table) : new SpellcastingTable();
    this.damage_type_overrides_table = obj && obj.damage_type_overrides_table ? new DamageTypeOverridesTable(obj.damage_type_overrides_table) : new DamageTypeOverridesTable();
    
    this.legendaries_table = obj && obj.legendaries_table ? new LegendariesTable(obj.legendaries_table) : new LegendariesTable();
    if (obj && obj.fbs) {
      this.fbs = obj.fbs.map((o: any) => { return new FeatureBase(o); });
    }
  }
}

export class DamageTypeOverridesTable {
  entries: DamageTypeOverrides[] = [];
  
  constructor(obj?: any) {
    if (obj) {
      this.entries = [];
      if (obj.entries) {
        obj.entries.forEach((entry: any) => {
          this.entries.push(new DamageTypeOverrides(entry));
        });
      }
    }
  }

  getDamageType(damage_type: DamageType) {
    const entry = this.entries.find(o => o.from_damage_types.includes(damage_type));
    if (entry) {
      return entry.to_damage_type;
    }
    return damage_type;
  }

  toDBObj(): any {
    return {
      entries: this.entries.map(o => o.toDBObj()),
    };
  }

  copy(copyMe: DamageTypeOverridesTable): void {
    this.entries = copyMe.entries.map(o => o.clone());
  }

  clone(): DamageTypeOverridesTable {
    const new_obj = new DamageTypeOverridesTable();
    new_obj.copy(this);
    return new_obj;
  }
}

export class DamageTypeOverrides { 
  true_id = "";
  name = "";
  description = "";
  from_damage_types: DamageType[] = [];
  to_damage_type: DamageType | "None" = "None";
  ability_limit: AbilityLimitation = new AbilityLimitation();
  name_formula = "";
  extra_effects: AbilityEffect[] = [];
  
  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.name = obj.name ? obj.name : "";
      this.description = obj.description ? obj.description : "";
      this.from_damage_types = obj.from_damage_types ? obj.from_damage_types : [];
      this.to_damage_type = obj.to_damage_type ? obj.to_damage_type : "None";
      this.ability_limit = obj.ability_limit ? new AbilityLimitation(obj.ability_limit) : new AbilityLimitation();
      this.name_formula = obj.name_formula ? obj.name_formula : "";
      // debugger;
      this.extra_effects = obj.extra_effects ? obj.extra_effects.map((o: any) => new AbilityEffect(o)) : [];
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      name: this.name,
      description: this.description,
      from_damage_types: this.from_damage_types,
      to_damage_type: this.to_damage_type,
      ability_limit: this.ability_limit.toDBObj(),
      name_formula: this.name_formula,
      extra_effects: this.extra_effects.map(o => o.toDBObj())
    };
  }

  copy(copyMe: DamageTypeOverrides): void {
    this.true_id = copyMe.true_id;
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.from_damage_types = [...copyMe.from_damage_types];
    this.to_damage_type = copyMe.to_damage_type;
    this.ability_limit = copyMe.ability_limit.clone();
    this.name_formula = copyMe.name_formula;
    this.extra_effects = copyMe.extra_effects.map(o => o.clone());
  }

  clone(): DamageTypeOverrides {
    const new_obj = new DamageTypeOverrides();
    new_obj.copy(this);
    return new_obj;
  }
}

export class CreatureGroup extends ModelBase {
  data_type: DataType = "creature_group";

  descriptor = "";
  treasure: string[] = [];
  habitats: string[] = [];
  settings: string[] = []; // Forgotten Realms, Eberron, Dragonlance, Ravenloft, Spelljammer, etc
  native_planes: string[] = []; 

  parse_errors: string[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.descriptor = obj.descriptor ? obj.descriptor : "";
      this.treasure = obj.treasure ? obj.treasure : [];
      this.habitats = obj.habitats ? obj.habitats : [];
      this.settings = obj.settings ? obj.settings : [];
      this.native_planes = obj.native_planes ? obj.native_planes : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.descriptor = obj.descriptor ? obj.descriptor : "";
  //     this.treasure = obj.treasure ? obj.treasure : [];
  //     this.habitats = obj.habitats ? obj.habitats : [];
  //     this.settings = obj.settings ? obj.settings : [];
  //     this.native_planes = obj.native_planes ? obj.native_planes : [];
  //   }
  // }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      descriptor: this.descriptor,
      treasure: this.treasure,
      habitats: this.habitats,
      settings: this.settings,
      native_planes: this.native_planes,
    };
  }

  copy(copyMe: CreatureGroup, drop_ids = false): void {
    super.copy(copyMe, drop_ids);

    this.descriptor = copyMe.descriptor;
    this.treasure = [...copyMe.treasure];
    this.habitats = [...copyMe.habitats];
    this.settings = [...copyMe.settings];
    this.native_planes = [...copyMe.native_planes];
  }

  clone(drop_ids = false): CreatureGroup {
    const new_obj = new CreatureGroup();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}